import React, { Component, useState, useRef, useEffect } from "react";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import * as Cookies from "es-cookie";
import { Row, Col, Button, Form, Modal } from "react-bootstrap";
// Same date-range picker the Expense dashboard uses.
import DateBlock from "../home_bck/DateBlock";
import {
  initials,
  timeAgo,
  formatDateTime,
  CATEGORIES,
  DOUBT_STATUS,
  STATUS_COLOR,
  isImageSrc,
} from "./SampleDoubtsData";
import { canEdit, canDelete } from "../../../utils/modulePermissions";
import {
  getUserScope,
  refreshUserScope,
  isUnscopedUser,
  idInScope,
  allowedSectionIds,
} from "../../../utils/dataScope";
import { scopeBranches, filterAuthorizedBranches, onBranchScopeChange } from "../../../utils/branchScope";

// Student Doubts Management — Staff dashboard (College Web Portal).
// Layout mirrors the Certificate dashboard: KPI cards on top, then a white
// panel with a Filters / Refresh / N-records header, dropdown filters, and a
// search + status-pill row above the doubt list.
// Backed by SampleDoubtsData so it renders with no backend.

const PURPLE = "#6366f1";

// Rows per page in the dashboard table.
const PAGE_SIZE = 10;

// This module, as it appears in the role's permission rows (roleModuleAccess).
// The sidebar/route gates key off module_key ("Student_Doubts"), but the rows
// carry the display name ("Doubts") too — and some resolvers return only that —
// so we accept either spelling when looking up is_edit / is_delete.
const DOUBTS_MODULE = ["Student_Doubts", "Doubts"];

// ── GraphQL: institute branches (with sections) + class master ────────────────
// Same queries the Certificate dashboard uses to drive its cascading filters.
const GET_BRANCHES = gql`
  query getBranches($institution_id: Int!) {
    getBranches(institution_id: $institution_id) {
      id
      branch_name
      sections { id class_id section_name stream_id }
    }
  }
`;

const GET_CLASSES = gql`
  query($id: Int) {
    getExamClasses(id: $id) { id class }
  }
`;

// Doubts raised from the mobile app (doubts_app + doubts_status), shown in the
// dashboard table. Status names already match DOUBT_STATUS
// (Pending / Reviewing / Completed / Irrelevent), so they map straight through.
const GET_STUDENT_DOUBTS = gql`
  query getStudentDoubts($institution_id: Int!, $branch_id: Int) {
    getStudentDoubts(institution_id: $institution_id, branch_id: $branch_id) {
      id
      title
      description
      image_path
      subject
      subject_name
      chapter
      chapter_name
      doubt_status
      status
      name
      mobile
      institution_id
      branch_id
      branch_name
      class_id
      class_name
      section_id
      section_name
      raised_time
      no_of_views
      is_public
      is_clarified
      assigned_faculty_id
      assigned_faculty_name
      answer_text
      answer_image
      answered_by
      answered_time
      awaiting_reply
      pending_reply_count
    }
  }
`;

// Master lists for the Subject / Chapter / Faculty filter dropdowns. Subject and
// chapter come from the neetjee taxonomy (neetjee.subject / neetjee.chapter) — the
// same ids the mobile app stores on a doubt. These come from the master tables (not
// the doubts), so the dropdowns are populated even when the institution has no
// doubts yet.
const GET_DOUBT_SUBJECTS = gql`
  query { getDoubtSubjects { id subject } }
`;
const GET_DOUBT_CHAPTERS = gql`
  query { getDoubtChapters { id chapter subject } }
`;
// Class → subject-ids mapping (education_mngt.class_subjects). Drives the
// class-based narrowing of the Subject dropdown.
const GET_DOUBT_CLASS_SUBJECTS = gql`
  query getDoubtClassSubjects($institution_id: Int!) {
    getDoubtClassSubjects(institution_id: $institution_id) { class_id subject_ids }
  }
`;
// Teaching faculty for the dashboard — a dedicated, self-contained doubts query
// that reads education_mngt.employee directly and returns only staff who teach a
// subject (subject_ids not empty). No dependency on getEmployeeDetails.
const GET_DOUBT_FACULTY = gql`
  query getDoubtFaculty($institution_id: Int!) {
    getDoubtFaculty(institution_id: $institution_id) {
      employee_id
      employee_name
      subject_ids
      subject_names
    }
  }
`;
// Persist a staff answer to a doubt (writes doubts_tracking + updates status).
const ANSWER_STUDENT_DOUBT = gql`
  mutation answerStudentDoubt($params: AnswerStudentDoubtInput) {
    answerStudentDoubt(params: $params)
  }
`;

// Edit an existing conversation message (a doubts_tracking row).
const UPDATE_DOUBT_MESSAGE = gql`
  mutation updateDoubtMessage($id: Int!, $answer: String, $image_path: String) {
    updateDoubtMessage(id: $id, answer: $answer, image_path: $image_path)
  }
`;

// Soft-delete a single conversation message (doubts_tracking.estatus = '0').
const DELETE_DOUBT_MESSAGE = gql`
  mutation deleteDoubtMessage($id: Int!) {
    deleteDoubtMessage(id: $id)
  }
`;

// Edit the original doubt question (doubts_app title / description).
const UPDATE_DOUBT_QUESTION = gql`
  mutation updateDoubtQuestion($doubts_id: Int!, $title: String, $description: String) {
    updateDoubtQuestion(doubts_id: $doubts_id, title: $title, description: $description)
  }
`;

// Soft-delete a student doubt (doubts_app.estatus = '0').
const DELETE_STUDENT_DOUBT = gql`
  mutation deleteStudentDoubt($doubts_id: Int!) {
    deleteStudentDoubt(doubts_id: $doubts_id)
  }
`;

// Reassign a doubt to the correct subject and/or faculty.
const REASSIGN_DOUBT = gql`
  mutation reassignDoubt($doubts_id: Int!, $subject_id: Int, $faculty_id: Int) {
    reassignDoubt(doubts_id: $doubts_id, subject_id: $subject_id, faculty_id: $faculty_id)
  }
`;

// Full conversation thread for one doubt — every doubts_tracking row (student
// follow-ups + faculty replies), oldest first. doubts_user_type tells us which
// side each message sits on (3 = faculty/lecturer, any other id = the student).
const GET_DOUBT_CONVERSATION = gql`
  query getDoubtConversation($doubts_id: Int!) {
    getDoubtConversation(doubts_id: $doubts_id) {
      id
      doubts_id
      answer
      image_path
      answered_time
      doubts_user_type
      user_type
      name
      mobile
      parent_tracking_id
      is_liked
      rating
    }
  }
`;

// ── Multi-select dropdown with checkboxes (matches Certificate dashboard) ──────
function MultiSelect({ label, icon, options, selected, onChange, onToggleOpen, isOpen, idKey = "id", labelKey = "label" }) {
  const containerRef = useRef(null);
  const [openAbove, setOpenAbove] = useState(false);

  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpenAbove(spaceAbove > spaceBelow && spaceBelow < 240);
    }
  }, [isOpen]);

  // "All selected" = every currently-visible option is selected. Using every()
  // (not a length match) keeps Select All checked / the label "All …" correct even
  // when `selected` holds more ids than the visible options (e.g. the Subject
  // selection still carries subjects that the active class filter has narrowed out).
  const allSelected = options.length > 0 && options.every((o) => selected.includes(String(o[idKey])));
  const noneSelected = selected.length === 0;
  // Checkboxes reflect the actual selection (no "empty == all" magic), so
  // Select All toggles both ways and you can deselect every option. An empty
  // selection still means "no filter" downstream in getVisible.
  const toggleAll = () => onChange(allSelected ? [] : options.map((o) => String(o[idKey])));
  const toggleOne = (id) => {
    const sid = String(id);
    onChange(selected.includes(sid) ? selected.filter((s) => s !== sid) : [...selected, sid]);
  };
  const displayLabel = noneSelected || allSelected ? `All ${label}` : `${selected.length} selected`;
  const dropdownPos = openAbove
    ? { bottom: "100%", top: "auto", marginTop: 0, marginBottom: 4 }
    : { top: "100%", bottom: "auto", marginTop: 4, marginBottom: 0 };

  return (
    <div ref={containerRef} style={{ position: "relative", zIndex: isOpen ? 200 : 1 }}>
      <div style={ms.label}>{icon && <i className={icon} style={{ marginRight: 5 }} />}{label}</div>
      <button type="button" onClick={onToggleOpen}
        style={{ ...ms.trigger, borderColor: isOpen ? PURPLE : "#d1d5db", boxShadow: isOpen ? `0 0 0 3px ${PURPLE}22` : "none" }}>
        <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#1f2937" }}>{displayLabel}</span>
        {!noneSelected && !allSelected && (
          <span style={{ background: PURPLE, color: "#fff", borderRadius: "50%", width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{selected.length}</span>
        )}
        <i className={`fal fa-chevron-${isOpen ? "up" : "down"}`} style={{ fontSize: 11, color: "#6b7280", flexShrink: 0 }} />
      </button>
      {isOpen && (
        <div style={{ ...ms.dropdown, ...dropdownPos }}>
          <label style={ms.checkRow}>
            <input type="checkbox" checked={allSelected} onChange={toggleAll} style={ms.checkbox} />
            <span style={{ ...ms.checkLabel, fontWeight: 700, color: "#374151" }}>Select All</span>
          </label>
          <div style={{ height: 1, background: "#e5e7eb", margin: "2px 0" }} />
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {options.length === 0 ? (
              <div style={{ padding: "10px 14px", fontSize: 12, color: "#9ca3af" }}>No options</div>
            ) : (
              options.map((opt) => {
                const sid = String(opt[idKey]);
                const checked = selected.includes(sid);
                return (
                  <label key={sid} style={{ ...ms.checkRow, background: checked ? `${PURPLE}08` : "transparent" }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleOne(opt[idKey])} style={ms.checkbox} />
                    <span style={{ ...ms.checkLabel, color: checked ? PURPLE : "#374151", fontWeight: checked ? 600 : 400 }}>{opt[labelKey]}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const ms = {
  label: { fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 5 },
  trigger: { width: "100%", display: "flex", alignItems: "center", gap: 8, background: "#fff", border: "1.5px solid #d1d5db", borderRadius: 8, padding: "8px 12px", fontSize: 13, cursor: "pointer", transition: "all 0.15s" },
  dropdown: { position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1000, background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", marginTop: 4, overflow: "hidden" },
  checkRow: { display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", cursor: "pointer", margin: 0, width: "100%" },
  checkbox: { width: 16, height: 16, accentColor: PURPLE, cursor: "pointer", flexShrink: 0 },
  checkLabel: { fontSize: 13, userSelect: "none" },
};

// KPI cards (Total / Pending / Reviewing / Completed / Irrelevent) — labels
// mirror the backend `doubts_status` table.
const STAT_CARDS = [
  { key: "All", label: "Total Doubts", color: PURPLE, icon: "fal fa-comments", sub: "All doubts" },
  {
    key: DOUBT_STATUS.PENDING,
    label: "Pending",
    color: STATUS_COLOR[DOUBT_STATUS.PENDING],
    icon: "fal fa-clock",
    sub: "Awaiting reply",
  },
  {
    key: DOUBT_STATUS.REVIEWING,
    label: "Reviewing",
    color: STATUS_COLOR[DOUBT_STATUS.REVIEWING],
    icon: "fal fa-eye",
    sub: "In review",
  },
  {
    key: DOUBT_STATUS.SOLVED,
    label: "Completed",
    color: STATUS_COLOR[DOUBT_STATUS.SOLVED],
    icon: "fal fa-check-circle",
    sub: "Resolved",
  },
  {
    key: DOUBT_STATUS.IRRELEVANT,
    label: "Irrelevent",
    color: STATUS_COLOR[DOUBT_STATUS.IRRELEVANT],
    icon: "fal fa-ban",
    sub: "Not relevant",
  },
];

// Top module tabs (certificate-style browser tabs).
const VIEW_TABS = [
  { key: "dashboard", label: "Dashboard", icon: "fal fa-tachometer-alt" },
  { key: "assignments", label: "Doubts Inbox", icon: "fal fa-inbox" },
];

const S = {
  page: { padding: "24px 24px 80px", width: "100%" },
  shell: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
    overflow: "hidden",
  },
  tabStrip: {
    background: "#f8fafc",
    borderBottom: "1.5px solid #e2e8f0",
    padding: "12px 24px 0 24px",
  },
  navList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  navTab: (active) => ({
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 14,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 20px",
    color: active ? PURPLE : "#4b5563",
    background: active ? "#fff" : "transparent",
    border: "1.5px solid",
    borderColor: active ? "#e2e8f0 #e2e8f0 #fff" : "transparent",
    borderBottomWidth: active ? "2px" : "1.5px",
    borderRadius: "10px 10px 0 0",
    transition: "all 0.15s ease",
    marginBottom: "-1.5px",
    textDecoration: "none",
  }),
  body: { padding: 24 },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: 16,
    marginBottom: 20,
  },
  kpiCard: (color) => ({
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderTop: `4px solid ${color}`,
    borderRadius: 14,
    padding: "16px 18px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    display: "flex",
    alignItems: "center",
    gap: 14,
  }),
  kpiIcon: (color) => ({
    background: `${color}15`,
    color,
    borderRadius: 10,
    width: 46,
    height: 46,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    fontSize: 20,
  }),
  kpiLabel: {
    fontSize: 10,
    color: "#6b7280",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  kpiValue: { fontSize: 24, fontWeight: 800, color: "#1f2937", lineHeight: 1.2 },
  kpiSub: { fontSize: 11, color: "#9ca3af" },

  panel: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    overflow: "hidden",
  },
  panelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    padding: "14px 20px",
    borderBottom: "1px solid #f1f5f9",
  },
  hIcon: {
    background: `${PURPLE}15`,
    color: PURPLE,
    borderRadius: 8,
    width: 30,
    height: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
  },
  refreshBtn: {
    display: "flex",
    alignItems: "center",
    background: "#fff",
    color: PURPLE,
    border: `1.5px solid ${PURPLE}55`,
    borderRadius: 8,
    padding: "5px 12px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  recordBadge: {
    background: `${PURPLE}15`,
    color: PURPLE,
    borderRadius: 20,
    padding: "4px 14px",
    fontSize: 13,
    fontWeight: 700,
  },
  filterBar: { padding: "16px 20px", borderBottom: "1px solid #f1f5f9" },
  filterGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: 14,
  },
  fLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 10,
    fontWeight: 700,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: 5,
  },
  fInput: {
    width: "100%",
    border: "1.5px solid #d1d5db",
    borderRadius: 8,
    padding: "9px 12px",
    fontSize: 13,
    color: "#1f2937",
    background: "#fff",
    outline: "none",
  },
  toolRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    padding: "12px 20px",
    borderBottom: "1px solid #f1f5f9",
  },
  search: {
    paddingLeft: 32,
    paddingRight: 12,
    paddingTop: 7,
    paddingBottom: 7,
    border: "1.5px solid #d1d5db",
    borderRadius: 8,
    fontSize: 13,
    color: "#1f2937",
    outline: "none",
    width: 260,
  },
  pill: (active, color) => ({
    border: `1.5px solid ${active ? color : "#e5e7eb"}`,
    borderRadius: 20,
    padding: "5px 14px",
    background: active ? color : "#fff",
    color: active ? "#fff" : "#4b5563",
    fontWeight: 600,
    fontSize: 13,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
  }),
  pillCount: (active, color) => ({
    background: active ? "rgba(255,255,255,0.25)" : `${color}22`,
    color: active ? "#fff" : color,
    borderRadius: 10,
    padding: "1px 7px",
    fontSize: 11,
    fontWeight: 700,
  }),

  // Table (Dashboard)
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    fontSize: 10,
    fontWeight: 700,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "2px solid #f1f5f9",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "10px 12px",
    borderBottom: "1px solid #f1f5f9",
    color: "#334155",
    verticalAlign: "middle",
  },

  listWrap: { padding: 16 },
  card: {
    border: "1px solid #eef0f2",
    borderRadius: 12,
    padding: "14px 16px",
    marginBottom: 12,
    background: "#fff",
    boxShadow: "0 1px 3px rgba(15,23,42,0.05)",
    cursor: "pointer",
  },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    fontSize: 12,
    fontWeight: 700,
    color: "#92400e",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  badge: (status) => ({
    fontSize: 11,
    fontWeight: 600,
    color: "#fff",
    background: STATUS_COLOR[status],
    borderRadius: 20,
    padding: "2px 10px",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
  }),
  dot: { width: 6, height: 6, borderRadius: "50%", background: "#fff" },
  meta: { fontSize: 12, color: "#64748b" },
  iconBtn: (color) => ({
    width: 30,
    height: 30,
    borderRadius: 8,
    border: `1px solid ${color}33`,
    background: `${color}12`,
    color,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    transition: "all 0.15s",
  }),
  // Small labelled action button (icon + text) for the table Actions column.
  actionBtn: (color) => ({
    height: 28,
    padding: "0 10px",
    borderRadius: 8,
    border: `1px solid ${color}33`,
    background: `${color}12`,
    color,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap",
  }),
  // Pagination button — active page is filled purple; disabled is muted.
  pageBtn: (active, disabled) => ({
    minWidth: 30,
    height: 30,
    padding: "0 8px",
    borderRadius: 8,
    border: `1px solid ${active ? PURPLE : "#e2e8f0"}`,
    background: active ? PURPLE : "#fff",
    color: active ? "#fff" : disabled ? "#cbd5e1" : "#475569",
    cursor: disabled ? "default" : "pointer",
    fontSize: 12,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  // Small clean count-pill button (icon + number) used in the workload table.
  miniBtn: (color, disabled) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    height: 26,
    padding: "0 10px",
    borderRadius: 7,
    border: `1px solid ${color}40`,
    background: disabled ? "#f8fafc" : `${color}12`,
    color: disabled ? "#cbd5e1" : color,
    fontSize: 12,
    fontWeight: 700,
    cursor: disabled ? "default" : "pointer",
    transition: "all 0.15s",
  }),
  imgTag: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 11,
    color: "#475569",
    background: "#f1f5f9",
    borderRadius: 6,
    padding: "2px 8px",
    marginTop: 6,
  },
  imgBox: {
    background: "#eff6ff",
    border: "1px dashed #bfdbfe",
    borderRadius: 8,
    color: "#3b82f6",
    fontSize: 13,
    textAlign: "center",
    padding: "26px 0",
    margin: "12px 0",
  },
  answerBox: {
    background: "#f0fdf4",
    borderLeft: "3px solid #22c55e",
    borderRadius: 6,
    padding: "10px 12px",
    marginTop: 10,
  },
};

const STATUS_PILLS = [
  "All",
  DOUBT_STATUS.PENDING,
  DOUBT_STATUS.REVIEWING,
  DOUBT_STATUS.SOLVED,
  DOUBT_STATUS.IRRELEVANT,
];

const avatarBg = (status) =>
  status === DOUBT_STATUS.SOLVED
    ? "#bbf7d0"
    : status === DOUBT_STATUS.REVIEWING
    ? "#bae6fd"
    : "#fde68a";

class StudentDoubtsComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Every doubt the backend returned, before the user's Branch/Class/Section/
      // Subject scope is applied. Kept so the scope can be re-derived once the
      // branch list loads (the section scope is derived from it) — see applyScope.
      allDoubts: [],
      // The doubts this user may actually see: allDoubts narrowed to their assigned
      // ids. Every card, count, table and workload view reads THIS list, so nothing
      // in the module can show a record outside the user's assignment.
      doubts: [],
      // Raw getStudentDoubts rows, kept so the per-user scope can be recomputed
      // once the subject/faculty masters load (see applyDoubtScope).
      allDoubtsRaw: [],
      activeView: "dashboard",
      activeTab: "All",
      searchText: "",
      fBranch: [],
      fClass: [],
      fSection: [],
      fSubject: [],
      fChapter: [],
      fFaculty: [],
      // Date-range filter on the doubt's raised date (empty = no bound).
      fDateFrom: "",
      fDateTo: "",
      // Drill-down: when set, the dashboard shows one student's doubts instead of
      // the unique-student list. Null = the grouped (one row per student) view.
      selectedStudentKey: null,
      openDropdown: null,
      // Faculty whose doubt list is expanded in the Assignments tab.
      expandedFaculty: null,
      // Search box for the Faculty Workload table (filters staff by name).
      facultySearch: "",
      // Doubt-text search on the Doubt Assignment (Faculty Workload) tab.
      assignDoubtSearch: "",
      // Search box for the "How doubts are assigned" table (subject / faculty).
      routingSearch: "",
      // "How doubts are assigned" panel is collapsed by default; click to open.
      showRoutingRules: false,
      // WhatsApp-style chat tab: currently open conversation + its search box.
      chatDoubtId: null,
      chatSearch: "",
      // Status card filter for the chat conversation list ("All" = no filter).
      chatStatusFilter: "All",
      // Real institute data driving the cascading Class/Section filters, already
      // narrowed to the header's branch scope.
      branches: [],
      // Every branch the user is authorized for — the "add doubt" form writes a
      // branch, so it must not be narrowed by the header's filter scope.
      allBranches: [],
      allClasses: [],
      // Master lists from the backend for the Subject/Chapter/Faculty filters.
      masterSubjects: [],
      masterChapters: [],
      masterFaculty: [],
      // class_subjects rows ({ class_id, subject_ids }) — narrows Subject by class.
      masterClassSubjects: [],
      // One-time guards so each filter group defaults to "all selected" once its
      // options have loaded (without re-checking boxes the user later unchecks).
      bcsInit: false, // Branch / Class / Section
      // Per-list guards: each Subject / Chapter / Faculty filter defaults to
      // "all selected" once ITS OWN list loads, independent of the others (the
      // three master lists load separately, so a single shared guard would leave
      // the slower lists unselected).
      subjInit: false,
      chapInit: false,
      facInit: false,
      // Dashboard table pagination (1-based current page + rows per page).
      page: 1,
      pageSize: PAGE_SIZE,
      // Id of the doubt pending delete (drives the in-app confirm dialog).
      deleteConfirmId: null,
      expandedId: null,
      modalDoubtId: null,
      // Conversation thread for the doubt currently being viewed (Answer modal OR
      // Faculty Workload modal): the doubts_tracking rows (student follow-ups +
      // faculty replies), the doubt id the thread belongs to (stale-fetch guard),
      // and a loading flag for the fetch.
      modalConversation: [],
      modalConvLoading: false,
      convDoubtId: null,
      // Inline message editing: which message is being edited and its draft text.
      // editingKey is `m<id>` for a tracking message or `q` for the original doubt.
      editingKey: null,
      editText: "",
      editTitle: "",
      // Id of the message showing its inline delete confirmation.
      deleteMsgId: null,
      // Faculty Workload "View doubts" popup: which faculty's popup is open,
      // which doubt inside it is selected, and whether it shows the solved or
      // the not-solved (open) doubts.
      workloadFaculty: null,
      // When set, the workload popup is scoped to one STUDENT (all their doubts)
      // instead of a faculty. Driven by the dashboard's "View doubts".
      workloadStudentKey: null,
      // Pending atomic reassign selection for the open doubt (subject + faculty),
      // applied together on the "Reassign" button. reassignDoubtId ties the pending
      // choice to a specific doubt so it doesn't leak across selections.
      reassignDoubtId: null,
      reassignSubject: "",
      reassignFaculty: "",
      // Search box in the inline student-doubts header (filters that student's list).
      studentDoubtSearch: "",
      // Status filter in the Assignment-tab inbox header: "all" | "unsolved" | "solved".
      assignFilter: "all",
      // Faculty Report tab: status filter + pagination.
      reportFilter: "all", // "all" | "solved" | "pending"
      reportPage: 1,
      workloadScope: "open",
      workloadDoubtId: null,
      replyText: "",
      answerImage: "",
      // Status staged in the workload solve dropdown; applied on Submit.
      replyStatus: "",
      showSubmit: false,
      // Subject / chapter / branch / faculty start empty and are populated from the
      // backend master lists (education_mngt) once they load. Chapter is optional;
      // faculty is the subject-based dropdown (auto-filled, override-able).
      newDoubt: { studentName: "", mobile: "", subject: "", chapter: "", branch: "", faculty: "", title: "", image: "" },
      nextId: 1000,
    };
    this.filterRef = React.createRef();
    // The selected row in the inbox's left list — used to scroll a deep-linked
    // doubt into view (see openDoubtById).
    this.activeDoubtRef = React.createRef();
  }

  async componentDidMount() {
    document.addEventListener("mousedown", this.handleOutsideClick);
    // Resolve the logged-in employee's assigned Class / Section / Subject ids FIRST,
    // from their own employee record (matched on the employee id cookie). Everything
    // below reads that scope — the doubts to show and the options each filter offers
    // — so fetching before it resolves would briefly build both from an empty scope.
    await refreshUserScope(this.props.client);
    this.fetchBranches();
    this.fetchClasses();
    const doubtsLoaded = this.fetchDoubts();
    this.fetchFilterMasters();
    // Deep link (dashboard → "Student Doubts & Clarifications" → View Details):
    // wait for the doubts to arrive, then open that one in the Doubts Inbox.
    if (this.props.initialDoubtId) {
      await doubtsLoaded;
      this.openDoubtById(this.props.initialDoubtId);
    }
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
    if (this.offBranchScope) this.offBranchScope();
  }

  // ── Role permissions (roleModuleAccess: is_edit / is_delete) ────────────────
  // Every action that MUTATES a doubt is gated on is_edit (answering, editing a
  // message, reassigning, changing status); removing one is gated on is_delete.
  // Admins and roles with no configured permissions keep full access — see can().
  // The buttons below are hidden when the grant is missing, and the handlers
  // re-check, so a stale render or a devtools click still can't write.
  canEditDoubts = () => canEdit(DOUBTS_MODULE);
  canDeleteDoubts = () => canDelete(DOUBTS_MODULE);

  // Close any open filter dropdown when clicking outside the filter bar.
  handleOutsideClick = (e) => {
    if (this.filterRef.current && !this.filterRef.current.contains(e.target))
      this.setState({ openDropdown: null });
  };

  // ── Load real institute data for the filter dropdowns (Certificate parity) ──
  fetchBranches = async () => {
    if (!this.props.client) return;
    try {
      const result = await this.props.client.query({
        query: GET_BRANCHES,
        variables: { institution_id: parseInt(Cookies.get("institutionid")) },
        fetchPolicy: "network-only",
      });
      const all = result.data.getBranches || [];
      this.setState({
        // The header's (NavbarOne) branch selection IS this page's branch filter —
        // every option and every doubt below is derived from this list, and it
        // never widens past the branches the user is authorized for.
        branches: scopeBranches(all),
        // The "add doubt" form writes a branch, so its options stay un-narrowed.
        allBranches: filterAuthorizedBranches(all),
        // Re-derive the Branch/Class/Section defaults against the new scope.
        bcsInit: false,
      }, () => {
        this.initBranchDefaults();
        // The section scope is derived from the branch list, so any doubt that
        // arrived before it must be re-checked now.
        this.applyScope();
      });
    } catch {
      /* ignore — dropdowns simply stay empty if the call fails */
    }
  };

  fetchClasses = async () => {
    if (!this.props.client) return;
    try {
      const result = await this.props.client.query({
        query: GET_CLASSES,
        variables: { id: 1 },
        fetchPolicy: "network-only",
      });
      this.setState({ allClasses: result.data.getExamClasses || [] }, this.initBranchDefaults);
    } catch {
      /* ignore */
    }
  };

  // Load the Subject / Chapter / Faculty master lists for the filter dropdowns.
  // These come from master tables, so the dropdowns are populated regardless of
  // whether this institution has any doubts.
  fetchFilterMasters = async () => {
    if (!this.props.client) return;
    const institution_id = parseInt(Cookies.get("institutionid"));

    // Load each master list independently — a failure in one (e.g. a backend
    // that has not yet picked up a schema change) must not blank the others.
    this.props.client
      .query({ query: GET_DOUBT_SUBJECTS, fetchPolicy: "network-only" })
      .then((subs) =>
        this.setState({ masterSubjects: subs.data.getDoubtSubjects || [] }, this.afterMasterLoad)
      )
      .catch((err) => console.warn("[StudentDoubts] subjects failed to load.", err));

    this.props.client
      .query({ query: GET_DOUBT_CHAPTERS, fetchPolicy: "network-only" })
      .then((chaps) =>
        this.setState({ masterChapters: chaps.data.getDoubtChapters || [] }, this.initMasterDefaults)
      )
      .catch((err) => console.warn("[StudentDoubts] chapters failed to load.", err));

    this.props.client
      .query({
        query: GET_DOUBT_CLASS_SUBJECTS,
        variables: { institution_id },
        fetchPolicy: "network-only",
      })
      .then((cs) =>
        // Re-default Subject/Faculty once the class→subject map arrives, so the
        // Subject dropdown reflects the class-based narrowing from the start.
        this.setState(
          { masterClassSubjects: cs.data.getDoubtClassSubjects || [], subjInit: false, chapInit: false, facInit: false },
          this.initMasterDefaults
        )
      )
      .catch((err) => console.warn("[StudentDoubts] class-subjects failed to load.", err));

    this.props.client
      .query({
        query: GET_DOUBT_FACULTY,
        variables: { institution_id },
        fetchPolicy: "network-only",
      })
      .then((fac) =>
        this.setState(
          {
            // getDoubtFaculty already returns only teaching faculty (staff with a
            // non-empty subject_ids). subjectIds drives the subject-based faculty
            // filtering and the subject→faculty auto-routing.
            masterFaculty: (fac.data.getDoubtFaculty || []).map((e) => ({
              id: e.employee_id,
              name: e.employee_name,
              subjectIds: String(e.subject_ids || "")
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
              // Subject NAMES (education_mngt.subject) for this employee — the
              // authoritative basis for scoping doubts by subject, independent of
              // the education_mngt ↔ neetjee subject-id divergence.
              subjectNames: String(e.subject_names || "")
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            })),
          },
          this.afterMasterLoad
        )
      )
      .catch((err) => console.warn("[StudentDoubts] faculty failed to load.", err));
  };

  // initMasterDefaults + re-scope the doubt list. Used as the load callback for the
  // subject and faculty masters — both are needed to (a) know which subjects the
  // logged-in employee teaches and (b) route each doubt to a subject teacher.
  afterMasterLoad = () => {
    this.initMasterDefaults();
    this.applyDoubtScope();
  };

  // The subject ids + names the logged-in employee teaches. Sourced from the
  // authoritative getDoubtFaculty mapping (education_mngt.employee.subject_ids) —
  // NOT the login `subject_ids` cookie, which can be stale/incomplete (the reported
  // case: the cookie carried 2 ids while getDoubtFaculty returns all 3, "1,3,2").
  // Falls back to the cookie only until masterFaculty has loaded. Names are
  // resolved via masterSubjects so doubts can be matched by subject name — the same
  // basis the Subject filter uses — instead of relying on id-space alignment.
  mySubjectScope = () => {
    const myId = parseInt(Cookies.get("id")) || null;
    const me = this.state.masterFaculty.find((f) => String(f.id) === String(myId));
    const ids =
      me && me.subjectIds.length
        ? me.subjectIds.map(String)
        : String(Cookies.get("subject_ids") || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
            .map(String);
    // Prefer the employee's real subject NAMES from getDoubtFaculty
    // (education_mngt.subject). Only fall back to resolving ids via neetjee
    // masterSubjects when names aren't available — that fallback is exactly what
    // dropped Chemistry, because the education_mngt id (e.g. 2) points at a
    // different neetjee subject than the doubt's neetjee subject id.
    const names =
      me && me.subjectNames && me.subjectNames.length
        ? me.subjectNames
        : this.state.masterSubjects
            .filter((s) => ids.includes(String(s.id)))
            .map((s) => s.subject);
    return { ids, names };
  };

  // Rebuild this.state.doubts from the raw backend rows: map each row, re-tag its
  // display faculty to a teacher of its subject, then scope the list to the
  // logged-in user (isMyDoubt). Re-run whenever the masters load, so a doubts
  // response that arrived before the masters gets re-scoped with the full
  // subject/faculty data (fixes the load-order race that could drop a subject).
  applyDoubtScope = () => {
    const rows = this.state.allDoubtsRaw || [];
    const haveMasters =
      this.state.masterFaculty.length > 0 && this.state.masterSubjects.length > 0;
    const myId = parseInt(Cookies.get("id")) || null;
    const mainCollege = this.isMainCollege();
    const mapped = rows.map((r) => {
      const d = this.mapDoubt(r);
      if (!haveMasters) return d;
      const related = this.facultyListForSubject(d.subject);
      const relatedNames = related.map((f) => f.name);
      // Keep only a REAL claim (assignedFacultyId) to a teacher of the subject.
      // We must NOT early-return on the display name alone: mapDoubt defaults an
      // UNCLAIMED doubt's assignedFaculty to the subject's PRIMARY teacher, which
      // is a valid teacher — so a name-only check would keep that primary tag and
      // skip the viewer-relative tagging below, leaving the logged-in teacher's
      // own doubts tagged to someone else (they then vanish from the workload).
      if (d.assignedFacultyId && relatedNames.includes(d.assignedFaculty)) return d;
      // Unclaimed shared doubt. In a subject-teacher's OWN login, show it under
      // THEIR name (never a co-teacher's) so the shared doubt reads as theirs to
      // answer. It stays UNCLAIMED (assignedFacultyId = null), so every teacher of
      // the subject still sees it until one of them replies. Management / non-
      // teachers fall back to the subject's primary (first) teacher.
      const me = related.find((f) => String(f.id) === String(myId));
      if (me && !mainCollege) {
        return { ...d, assignedFaculty: me.name };
      }
      return { ...d, assignedFaculty: relatedNames[0] || "Unassigned" };
    });
    this.setState({ doubts: mapped.filter(this.isMyDoubt) });
  };

  // Default Branch / Class / Section to all-selected once both branches and
  // classes have loaded (runs once via the bcsInit guard).
  initBranchDefaults = () => {
    const { branches, allClasses, bcsInit } = this.state;
    if (bcsInit || branches.length === 0 || allClasses.length === 0) return;
    const { branchOptions, classOptions, sectionOptions } = this.getFilterOptions();
    this.setState(
      {
        bcsInit: true,
        fBranch: branchOptions.map((o) => String(o.id)),
        fClass: classOptions.map((o) => String(o.id)),
        fSection: sectionOptions.map((o) => String(o.id)),
        // Classes just became known — re-default Subject/Chapter/Faculty so their
        // selection matches the now class-narrowed Subject options (avoids the
        // "all 66 subjects selected" vs fewer-options mismatch on first load).
        subjInit: false,
        chapInit: false,
        facInit: false,
      },
      this.initMasterDefaults
    );
  };

  // Default Subject / Chapter / Faculty to all-selected. Safe to call repeatedly:
  // each filter defaults exactly once, when its own list first has options, so it
  // works no matter which of the three master queries resolves first.
  initMasterDefaults = () => {
    const { subjInit, chapInit, facInit } = this.state;
    const { subjectOptions, chapterOptions, facultyOptions } = this.getFilterOptions();
    const next = {};
    if (!subjInit && subjectOptions.length > 0) {
      next.fSubject = subjectOptions.map((o) => String(o.id));
      next.subjInit = true;
    }
    if (!chapInit && chapterOptions.length > 0) {
      next.fChapter = chapterOptions.map((o) => String(o.id));
      next.chapInit = true;
    }
    if (!facInit && facultyOptions.length > 0) {
      next.fFaculty = facultyOptions.map((o) => String(o.id));
      next.facInit = true;
    }
    if (Object.keys(next).length > 0) this.setState(next);
  };

  // Map a backend doubts_app row to the shape the dashboard cards/table expect.
  // The mobile app stores ids; the resolver already joined the display names.
  mapDoubt = (r) => ({
    id: r.id,
    studentName: r.name || "Student",
    admissionNo: r.admission_no || "",
    rollNo: "",
    branch: r.branch_name || "",
    branchId: r.branch_id || null,
    class: r.class_name || "",
    // The ids (not just the display names) the access scope is matched on.
    classId: r.class_id || null,
    section: r.section_name || "",
    sectionId: r.section_id || null,
    subject: r.subject_name || "",
    subjectId: r.subject || null,
    chapter: r.chapter_name || "",
    category: "",
    mobile: r.mobile || "",
    views: Number(r.no_of_views) || 0,
    isPublic: r.is_public === "1",
    title: r.title || "",
    description: r.description || "",
    images: r.image_path ? [r.image_path] : [],
    // doubts_status names already match DOUBT_STATUS values.
    status: r.status || DOUBT_STATUS.PENDING,
    // Manually reassigned faculty wins; otherwise subject-based auto-routing tags
    // the doubt to the faculty who teaches its subject.
    assignedFaculty: r.assigned_faculty_name || this.facultyForSubject(r.subject_name) || "Unassigned",
    assignedFacultyId: r.assigned_faculty_id || null,
    createdAt: r.raised_time || "",
    // True when the student re-asked after being answered (latest thread message is
    // the student's) — drives the "New student reply" badge and pulls a Completed
    // doubt back into the not-solved view.
    awaitingReply: r.awaiting_reply === "1",
    // Count of unanswered student messages — shown as the red/orange count badge.
    pendingCount: Number(r.pending_reply_count) || 0,
    // Latest saved answer from doubts_tracking (null when not yet answered).
    answer:
      r.answer_text || r.answer_image
        ? {
            text: r.answer_text || "",
            image: r.answer_image || "",
            answeredBy: r.answered_by || "Faculty",
            answeredAt: r.answered_time || "",
          }
        : null,
  });

  // Load real doubts from the backend. The table ALWAYS reflects the backend —
  // it never falls back to sample/demo data, so what staff see is the logged-in
  // institution's real doubts (or an empty table), never misleading fake rows.
  fetchDoubts = async () => {
    if (!this.props.client) return;
    try {
      // Fetch the institution's doubts unfiltered and let applyScope() narrow them.
      // Don't pass branch_id: the server-side filter is an extra failure mode (a
      // doubt whose branch_id doesn't match empties the whole table) and it buys no
      // security, since the value comes from the client either way.
      const result = await this.props.client.query({
        query: GET_STUDENT_DOUBTS,
        variables: { institution_id: parseInt(Cookies.get("institutionid")) },
        fetchPolicy: "network-only",
      });
      const rows = result.data.getStudentDoubts || [];
      // Keep the raw rows and (re)build the scoped list. applyDoubtScope scopes with
      // whatever masters are loaded now (cookie fallback), and afterMasterLoad runs
      // it again once getDoubtFaculty/getDoubtSubjects arrive for full accuracy.
      this.setState({ allDoubtsRaw: rows }, this.applyDoubtScope);
      // Keep the raw rows and derive the visible set from them, so the scope can be
      // re-applied when the branch list (which the section scope needs) arrives.
      this.setState({ allDoubts: rows.map(this.mapDoubt) }, this.applyScope);
      if (rows.length === 0) {
        console.warn(
          "[StudentDoubts] no doubts for institution",
          Cookies.get("institutionid")
        );
      }
    } catch (err) {
      // Surface the real error and clear the table — do NOT mask the failure with
      // sample data. If you see an empty table, the error here says why (commonly:
      // the backend has not reloaded the latest StudentDoubts schema yet).
      console.error("[StudentDoubts] getStudentDoubts failed — clearing table.", err);
      this.setState({ allDoubts: [], doubts: [] });
    }
  };

  // Narrow the backend rows to what this user is allowed to see. Re-run whenever
  // the inputs change (the rows arrive, or `branches` loads — the section scope is
  // derived from it), so a doubt fetched before the branches can't stay visible.
  applyScope = () => {
    this.setState((prev) => {
      const all = prev.allDoubts || [];
      const doubts = all.filter(this.isMyDoubt);
      // When the scope hides everything, say WHICH dimension did it. Silently showing
      // an empty table is indistinguishable from "this institute has no doubts", and
      // a single mismatched id (a doubt with no class_id, a class the employee isn't
      // assigned) is otherwise invisible.
      if (all.length > 0 && doubts.length === 0 && !isUnscopedUser()) {
        const scope = getUserScope();
        const dropped = (dim, ids, pick) =>
          ids.length === 0 ? 0 : all.filter((d) => !idInScope(ids, pick(d))).length;
        console.warn(
          "[StudentDoubts] the access scope hid all " + all.length + " doubts.",
          {
            scope,
            hiddenByBranch: dropped("branch", scope.branchIds, (d) => d.branchId),
            hiddenByClass: dropped("class", scope.classIds, (d) => d.classId),
            hiddenBySubject: dropped("subject", scope.subjectIds, (d) => d.subjectId),
            sampleDoubt: all[0],
          }
        );
      }
      return { doubts };
    });
  };

  // The subjects taught in the given classes, via the education_mngt.class_subjects
  // map (subject_ids align with the neetjee subject ids in masterSubjects). Falls
  // back to all subjects when there is no class_subjects data or no class selected.
  allowedSubjectsForClasses = (classIds) => {
    const { masterClassSubjects, masterSubjects } = this.state;
    const scope = getUserScope();

    // An assigned employee's subjects ARE their assignment (employee.subject_ids) —
    // offer exactly those. Do NOT narrow them further by the class_subjects map: that
    // mapping is incomplete for many institutes, so a teacher assigned 5 subjects
    // would silently be shown only the 3 that happen to be mapped to their classes.
    // The assignment is the authority; the mapping is not.
    if (!isUnscopedUser() && scope.subjectIds.length > 0) {
      return masterSubjects.filter((s) => idInScope(scope.subjectIds, s.id));
    }

    // Unassigned user / admin: fall back to narrowing by the selected classes.
    if (masterClassSubjects.length === 0 || !classIds || classIds.length === 0) {
      return masterSubjects;
    }
    const allowed = new Set();
    masterClassSubjects
      .filter((cs) => classIds.includes(String(cs.class_id)))
      .forEach((cs) =>
        String(cs.subject_ids || "")
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean)
          .forEach((id) => allowed.add(id))
      );
    return masterSubjects.filter((s) => allowed.has(String(s.id)));
  };

  // Derive the cascading Branch → Class → Section options from real data.
  // Mirrors the Certificate dashboard: classes/sections narrow to the selected
  // branches, and sections further narrow to the selected classes.
  getFilterOptions = () => {
    const { branches, allClasses, fBranch, fClass } = this.state;

    // The dropdowns may only OFFER what this user is assigned — otherwise the filter
    // bar advertises branches / classes / sections / subjects whose records they
    // can't see anyway, and "All Branches" would read as the whole institute.
    const scope = getUserScope();
    const unscoped = isUnscopedUser();
    const myBranches = unscoped
      ? branches
      : branches.filter((b) => idInScope(scope.branchIds, b.id));
    const mySections = unscoped ? null : allowedSectionIds(branches, scope);

    const selectedBranchObjs =
      fBranch.length > 0 ? myBranches.filter((b) => fBranch.includes(String(b.id))) : myBranches;

    const branchOptions = myBranches.map((b) => ({ id: b.id, label: b.branch_name }));

    const classIdSet = new Set(
      selectedBranchObjs.flatMap((b) => b.sections || []).map((s) => String(s.class_id))
    );
    // The employee's assignment is AUTHORITATIVE: an assigned class is always offered.
    // Don't also require it to appear in the branch's section rows — an institute with
    // no section mapped to that class would leave the dropdown empty ("No options")
    // and lock the user out of their own class. Only an unassigned user (or an admin)
    // falls back to deriving the list from the selected branches' sections.
    const scopedToClasses = !unscoped && scope.classIds.length > 0;
    const classOptions = allClasses
      .filter((c) =>
        scopedToClasses ? idInScope(scope.classIds, c.id) : classIdSet.has(String(c.id))
      )
      .map((c) => ({ id: c.id, label: c.class }));

    // Sections of the in-scope branches, narrowed to the selected classes.
    const branchSections = selectedBranchObjs
      .flatMap((b) => b.sections || [])
      .filter((s) => !mySections || idInScope(mySections, s.id));
    const classSections = branchSections.filter(
      (s) => fClass.length === 0 || fClass.includes(String(s.class_id))
    );
    // If the branch has no section row for the selected class, narrowing by class
    // leaves nothing and the dropdown reads "No options". Fall back to the branch's
    // sections so the user can still filter — the records themselves stay scoped.
    const sectionOptions = (classSections.length > 0 ? classSections : branchSections).map((s) => ({
      id: s.id,
      label: s.section_name,
    }));

    // Subject / Chapter / Faculty options come from the backend master lists so
    // the dropdowns are populated even when the institution has no doubts. The
    // option id is the display name (subject/chapter/faculty name) so the
    // existing name-based filtering in getVisible keeps working unchanged.
    const { masterSubjects, masterChapters, fSubject } = this.state;

    // Class-based subjects: when classes are selected, the Subject dropdown only
    // offers subjects mapped to those classes (education_mngt.class_subjects).
    const subjectObjs = this.allowedSubjectsForClasses(fClass);
    const subjectOptions = subjectObjs.map((s) => ({ id: s.subject, label: s.subject }));

    // Ids of the currently-selected subjects (fSubject holds subject names).
    const selectedSubjectIds = masterSubjects
      .filter((s) => fSubject.includes(s.subject))
      .map((s) => String(s.id));

    // Subject-based chapters: chapters narrow to the selected subjects via the
    // chapter's `subject` fk (neetjee.chapter.subject -> neetjee.subject.id).
    const chapterSource =
      fSubject.length > 0
        ? masterChapters.filter((c) => selectedSubjectIds.includes(String(c.subject)))
        : masterChapters;
    const chapterOptions = chapterSource.map((c) => ({ id: c.chapter, label: c.chapter }));

    // Faculty: a scoped employee only ever sees THEMSELVES here — the other staff are
    // not theirs to filter by. Admins get the full list, narrowed to the staff who
    // teach a selected subject (education_mngt.employee.subject_ids).
    const myFaculty = this.visibleFaculty();
    const facultySource =
      unscoped && fSubject.length > 0
        ? myFaculty.filter((f) =>
            f.subjectIds.some((sid) => selectedSubjectIds.includes(String(sid)))
          )
        : myFaculty;
    const facultyOptions = facultySource.map((f) => ({ id: f.name, label: f.name }));

    return { branchOptions, classOptions, sectionOptions, subjectOptions, chapterOptions, facultyOptions };
  };

  toggleExpand = (id) => {
    this.setState((prev) => {
      const opening = prev.expandedId !== id;
      const ans = opening ? prev.doubts.find((d) => d.id === id).answer || {} : {};
      return {
        expandedId: opening ? id : null,
        replyText: opening ? ans.text || "" : "",
        answerImage: opening ? ans.image || "" : "",
      };
    });
  };

  openAnswerModal = (d) => {
    // A reply always starts the next message in the thread — don't prefill the box
    // with the last answer (that was for the edit-in-place model; we now append).
    this.setState({
      modalDoubtId: d.id,
      replyText: "",
      answerImage: "",
      replyStatus: this.workloadStatusOf(d),
      modalConversation: [],
    });
    this.fetchConversation(d.id);
  };

  closeAnswerModal = () => {
    this.setState({
      modalDoubtId: null,
      replyText: "",
      answerImage: "",
      replyStatus: "",
      modalConversation: [],
      convDoubtId: null,
      editingKey: null,
      editText: "",
      editTitle: "",
    });
  };

  // Load the full conversation thread (all doubts_tracking rows) for a doubt.
  // Shared by the Answer modal and the Faculty Workload modal; convDoubtId records
  // which doubt the thread is for so a slow response can't overwrite a newer one.
  fetchConversation = async (doubtId) => {
    if (!this.props.client || !doubtId) return;
    this.setState({ modalConvLoading: true, convDoubtId: doubtId, modalConversation: [], editingKey: null, editText: "", editTitle: "", deleteMsgId: null });
    try {
      const result = await this.props.client.query({
        query: GET_DOUBT_CONVERSATION,
        variables: { doubts_id: doubtId },
        fetchPolicy: "network-only",
      });
      // Guard against a stale response if the viewed doubt changed meanwhile.
      if (this.state.convDoubtId !== doubtId) return;
      this.setState({
        modalConversation: result.data.getDoubtConversation || [],
        modalConvLoading: false,
      });
    } catch (err) {
      console.warn("[StudentDoubts] getDoubtConversation failed.", err);
      if (this.state.convDoubtId === doubtId) {
        this.setState({ modalConversation: [], modalConvLoading: false });
      }
    }
  };

  // ── Inline message editing ────────────────────────────────────────────────
  // Start editing a conversation message (a doubts_tracking row).
  startEditMessage = (m) => {
    if (!this.canEditDoubts()) return;
    this.setState({ editingKey: `m${m.id}`, editText: m.answer || "", editTitle: "" });
  };
  // Start editing the original doubt question (doubts_app).
  startEditQuestion = (d) => {
    if (!this.canEditDoubts()) return;
    this.setState({ editingKey: "q", editText: d.description || "", editTitle: d.title || "" });
  };
  cancelEdit = () => this.setState({ editingKey: null, editText: "", editTitle: "", deleteMsgId: null });

  // Inline per-message delete confirmation.
  askDeleteMessage = (m) => {
    if (!this.canDeleteDoubts()) return;
    this.setState({ deleteMsgId: m.id, editingKey: null });
  };
  cancelDeleteMessage = () => this.setState({ deleteMsgId: null });
  // Confirmed → soft-delete the message (doubts_tracking.estatus = '0'), reload thread.
  confirmDeleteMessage = (m) => {
    if (!this.props.client || !this.canDeleteDoubts()) return;
    this.props.client
      .mutate({ mutation: DELETE_DOUBT_MESSAGE, variables: { id: m.id } })
      .then(() => {
        const id = this.state.convDoubtId;
        this.setState({ deleteMsgId: null });
        if (id) this.fetchConversation(id);
      })
      .catch((err) => console.warn("[StudentDoubts] deleteDoubtMessage failed.", err));
  };

  // Save an edited conversation message → update doubts_tracking, then reload thread.
  saveEditMessage = (m) => {
    if (!this.props.client || !this.canEditDoubts()) return;
    const answer = this.state.editText;
    this.props.client
      .mutate({ mutation: UPDATE_DOUBT_MESSAGE, variables: { id: m.id, answer } })
      .then(() => {
        const id = this.state.convDoubtId;
        this.cancelEdit();
        if (id) this.fetchConversation(id);
      })
      .catch((err) => console.warn("[StudentDoubts] updateDoubtMessage failed.", err));
  };

  // Save the edited original doubt → update doubts_app, then refresh table + thread.
  saveEditQuestion = (d) => {
    if (!this.props.client || !this.canEditDoubts()) return;
    const title = this.state.editTitle;
    const description = this.state.editText;
    this.props.client
      .mutate({ mutation: UPDATE_DOUBT_QUESTION, variables: { doubts_id: d.id, title, description } })
      .then(() => {
        const id = this.state.convDoubtId;
        this.cancelEdit();
        this.fetchDoubts();
        if (id) this.fetchConversation(id);
      })
      .catch((err) => console.warn("[StudentDoubts] updateDoubtQuestion failed.", err));
  };

  // Which side of the thread a message sits on. Faculty replies from this portal
  // carry doubts_user_type = 3 (College Lecturer); any other user type — or a
  // user_type label that reads as staff — is the student/app side on the left.
  isFacultyMessage = (m) => {
    if (Number(m.doubts_user_type) === 3) return true;
    const t = String(m.user_type || "").toLowerCase();
    return /lectur|facul|teach|staff|tutor|admin/.test(t);
  };

  // Send a reply from the Answer modal: append the answer to the thread and apply
  // the selected status. The modal STAYS OPEN so staff see their message land in
  // the conversation and can keep replying; the thread is re-fetched after saving.
  submitAnswerModal = (id) => {
    if (!this.canEditDoubts()) return;
    const status = this.state.replyStatus;
    const text = this.state.replyText.trim();
    const image = this.state.answerImage;
    if (!status && !text && !image) return;
    const doubt = this.state.doubts.find((d) => d.id === id);
    // Claim the doubt for whoever ACTUALLY answers, so a shared-pool doubt leaves
    // the OTHER teachers' views the moment one of them replies:
    //   • already claimed → keep it with the current owner.
    //   • unclaimed + a teacher of the subject is logged in → that logged-in
    //     teacher (the one replying) claims it — not the subject's primary.
    //   • otherwise (management / non-teacher replying) → claim for the subject's
    //     primary teacher so a real teacher owns it, not the management account.
    const myId = parseInt(Cookies.get("id")) || null;
    const myName = Cookies.get("name") || "";
    const teachers = doubt ? this.facultyListForSubject(doubt.subject) : [];
    const iTeach = teachers.some((x) => String(x.id) === String(myId));
    let answerer;
    if (doubt && doubt.assignedFacultyId) {
      const owner = this.state.masterFaculty.find((x) => String(x.id) === String(doubt.assignedFacultyId));
      answerer = { name: owner ? owner.name : doubt.assignedFaculty, id: doubt.assignedFacultyId };
    } else if (iTeach) {
      answerer = { name: myName, id: myId };
    } else {
      const primary = teachers[0];
      answerer = primary ? { name: primary.name, id: primary.id } : { name: myName, id: myId };
    }
    const claimName = answerer.name;
    const claiming = !!(text || image) && !!answerer.id;
    // Terminal close (Completed/Irrelevent): settle the doubt locally even when a
    // student message is last, so it doesn't linger in the not-solved view.
    const terminal = status === DOUBT_STATUS.SOLVED || status === DOUBT_STATUS.IRRELEVANT;
    // Persist the answer + status to the backend (doubts_tracking / doubts_app).
    const saved = this.saveDoubtAnswerToBackend(doubt, status, text, image, answerer);
    // Clear the reply box immediately, keep the modal open, and reflect the new
    // status + claim on the dashboard row.
    this.setState((prev) => ({
      replyText: "",
      answerImage: "",
      replyStatus: "",
      doubts: prev.doubts.map((d) => {
        if (d.id !== id) return d;
        const hasAnswer = !!(text || image);
        return {
          ...d,
          status: status || d.status,
          awaitingReply: terminal ? false : d.awaitingReply,
          pendingCount: terminal ? 0 : d.pendingCount,
          assignedFaculty: claiming ? claimName : d.assignedFaculty,
          assignedFacultyId: claiming ? answerer.id : d.assignedFacultyId,
          answer: hasAnswer
            ? { text, image, answeredBy: claimName || "Faculty", answeredAt: new Date().toISOString() }
            : d.answer,
        };
      }),
    }));
    // Once the reply row is written, reload the thread so the new bubble appears.
    if (text || image) {
      Promise.resolve(saved).then(() => {
        if (this.state.modalDoubtId === id) this.fetchConversation(id);
      });
    }
  };

  // ── Faculty Workload "View doubts" popup ───────────────────────────────────
  // Opens a popup listing all of a faculty's doubts; staff click one to open
  // its solve fields and can solve several before closing.
  // Does a doubt belong in the current popup scope? "solved" → solved only,
  // "open" → pending/reviewing only.
  // A doubt awaiting a faculty reply (student re-asked) always counts as "not
  // solved" — even if its status is Completed — so it surfaces in the open list.
  inWorkloadScope = (d, scope) =>
    scope === "solved"
      ? d.status === DOUBT_STATUS.SOLVED && !d.awaitingReply
      : d.status !== DOUBT_STATUS.SOLVED || d.awaitingReply;

  // Statuses offered by the workload solve dropdown.
  // Status pre-selected in the composer dropdown when a doubt is opened — the
  // doubt's current status (so a newly-raised doubt shows "Pending" by default).
  workloadStatusOf = (d) => (d && d.status) || DOUBT_STATUS.PENDING;

  // Open the popup for a faculty, scoped to either solved or not-solved doubts.
  openWorkloadModal = (f, scope = "open") => {
    const list = this.state.doubts.filter(
      (d) => this.facultyOwnsDoubt(f, d) && this.inWorkloadScope(d, scope)
    );
    const first = list[0] || null;
    // A reply always starts the next message in the thread — don't prefill the box.
    this.setState({
      workloadFaculty: f,
      workloadScope: scope,
      workloadDoubtId: first ? first.id : null,
      replyText: "",
      answerImage: "",
      replyStatus: this.workloadStatusOf(first),
      modalConversation: [],
      convDoubtId: null,
    });
    if (first) this.fetchConversation(first.id);
  };

  // Open the same popup scoped to ONE STUDENT — lists ALL that student's doubts
  // (any status) on the left and their conversation on the right. Used by the
  // dashboard's "View doubts": it switches to the Doubt Assignment tab and opens
  // this straight away. workloadStudentKey (not workloadFaculty) drives the scope.
  openStudentDoubts = (studentKey) => {
    const list = this.state.doubts.filter((d) => this.studentKey(d) === studentKey);
    const first = list[0] || null;
    this.setState({
      activeView: "assignments",
      workloadStudentKey: studentKey,
      studentDoubtSearch: "",
      workloadFaculty: null,
      workloadScope: "open",
      workloadDoubtId: first ? first.id : null,
      replyText: "",
      answerImage: "",
      replyStatus: this.workloadStatusOf(first),
      modalConversation: [],
      convDoubtId: null,
    });
    if (first) this.fetchConversation(first.id);
  };

  // Open the Doubts Inbox with ONE doubt selected, given its id — the entry point
  // for deep links from the dashboards ("View Details" on a doubt row). The lookup
  // runs inside the state updater so it sees the list as fetchDoubts/applyScope
  // just left it, rather than a stale this.state snapshot.
  openDoubtById = (doubtId) => {
    let target = null;
    this.setState(
      (prev) => {
        target = (prev.doubts || []).find((d) => String(d.id) === String(doubtId)) || null;
        return {
          activeView: "assignments",
          // Plain inbox mode: every doubt on the left, no faculty/student scoping
          // and no status filter that could hide the doubt being opened.
          workloadFaculty: null,
          workloadStudentKey: null,
          studentDoubtSearch: "",
          assignFilter: "all",
          workloadDoubtId: target ? target.id : null,
          replyText: "",
          answerImage: "",
          replyStatus: this.workloadStatusOf(target),
          modalConversation: [],
          convDoubtId: null,
        };
      },
      () => {
        if (!target) {
          // The inbox still opens (empty right pane) — say why, since a doubt the
          // access scope hides is otherwise indistinguishable from a bad link.
          console.warn("[StudentDoubts] doubt " + doubtId + " is not in this user's visible list.");
          return;
        }
        this.fetchConversation(target.id);
        // The list is every doubt in scope, so the opened one can be far down it.
        if (this.activeDoubtRef.current && this.activeDoubtRef.current.scrollIntoView) {
          this.activeDoubtRef.current.scrollIntoView({ block: "center" });
        }
      }
    );
  };

  closeWorkloadModal = () => {
    this.setState({
      workloadFaculty: null,
      workloadStudentKey: null,
      studentDoubtSearch: "",
      workloadDoubtId: null,
      replyText: "",
      answerImage: "",
      replyStatus: "",
      modalConversation: [],
      convDoubtId: null,
      editingKey: null,
      editText: "",
      editTitle: "",
    });
  };

  // Select a doubt inside the popup — load its conversation thread + current status.
  selectWorkloadDoubt = (d) => {
    this.setState({
      workloadDoubtId: d.id,
      replyText: "",
      answerImage: "",
      replyStatus: this.workloadStatusOf(d),
    });
    this.fetchConversation(d.id);
  };

  // Reassign a doubt to another staff from inside the popup. Once it moves to a
  // DIFFERENT faculty the reassignment is complete, so refresh the view to the next
  // doubt in this faculty's scope — or an empty state when there are none left.
  reassignWorkloadDoubt = (id, faculty) => {
    if (!faculty) return;
    // Inline views (student or all-doubts inbox): the doubt stays in the list, so
    // just apply the reassignment and keep it selected. Only the legacy faculty
    // modal jumps to the next doubt.
    if (!this.state.workloadFaculty) {
      this.reassignFaculty(id, faculty);
      return;
    }
    const f = this.state.workloadFaculty;
    const scope = this.state.workloadScope;
    const next =
      this.state.doubts.find(
        (d) => this.facultyOwnsDoubt(f, d) && d.id !== id && this.inWorkloadScope(d, scope)
      ) || null;
    this.reassignFaculty(id, faculty);
    if (faculty !== f) {
      if (next) this.selectWorkloadDoubt(next);
      else this.setState({ workloadDoubtId: null });
    }
  };

  // Persist a staff answer to the backend: writes the answer row into doubts_tracking
  // and updates the doubt's status (doubts_app). The College web portal answerer is a
  // College Lecturer (doubts_user_type = 3, set server-side).
  saveDoubtAnswerToBackend = (doubt, status, text, image, answerer) => {
    if (!this.props.client || !doubt) return Promise.resolve();
    // The faculty answering claims the doubt (shared pool → this one). Priority:
    // explicit answerer → the doubt's current/assigned faculty → the LOGGED-IN
    // faculty. The last fallback is what makes the FIRST teacher to reply on an
    // unclaimed shared-pool doubt claim it (so the other subject teachers stop
    // seeing it), which is the core of the subject-scoped doubts workflow.
    const loggedInId = parseInt(Cookies.get("id")) || null;
    const loggedInName = Cookies.get("name") || "";
    const facultyName =
      (answerer && answerer.name) ||
      (doubt.assignedFaculty && doubt.assignedFaculty !== "Unassigned" ? doubt.assignedFaculty : "") ||
      loggedInName;
    const facultyId =
      (answerer && answerer.id) ||
      doubt.assignedFacultyId ||
      (this.state.masterFaculty.find((x) => x.name === facultyName) || {}).id ||
      loggedInId;
    return this.props.client
      .mutate({
        mutation: ANSWER_STUDENT_DOUBT,
        variables: {
          params: {
            doubts_id: doubt.id,
            answer: text || "",
            image_path: image || "",
            // The answering faculty (the staff handling the doubt) — claims it.
            name: facultyName,
            faculty_id: facultyId,
            mobile: "",
            institution_id: parseInt(Cookies.get("institutionid")) || null,
            branch_id: doubt.branchId || null,
            doubt_status: status || "",
          },
        },
      })
      // After the answer/status is saved, re-pull the dashboard table so it shows
      // the persisted answer and updated status.
      .then(() => this.fetchDoubts())
      .catch((err) => console.warn("[StudentDoubts] answerStudentDoubt failed.", err));
  };

  // Submit: save the selected status (dropdown) and any answer for the doubt, so it
  // moves to that status card, then jump to the next doubt in the same scope so
  // staff can keep going without closing the popup.
  submitWorkloadAnswer = (id) => {
    if (!this.canEditDoubts()) return;
    const status = this.state.replyStatus;
    const text = this.state.replyText.trim();
    const image = this.state.answerImage;
    // Need at least a status choice or an answer to do anything.
    if (!status && !text && !image) return;

    // Who is answering CLAIMS the doubt (so the other subject teachers no longer
    // see it). Faculty-modal mode → the faculty whose workload is open. Inbox /
    // student mode (no workloadFaculty) → the LOGGED-IN faculty. If the logged-in
    // user isn't a teaching faculty (e.g. management), leave the id null so
    // saveDoubtAnswerToBackend falls back to the doubt's assigned/subject faculty.
    const f = this.state.workloadFaculty;
    let answerer;
    if (f) {
      const facObj = this.state.masterFaculty.find((x) => x.name === f);
      answerer = { name: f, id: facObj ? facObj.id : null };
    } else {
      const myId = parseInt(Cookies.get("id")) || null;
      const me = this.state.masterFaculty.find((x) => String(x.id) === String(myId));
      answerer = me ? { name: me.name, id: me.id } : { name: "", id: null };
    }
    const claiming = !!(text || image); // only claim when actually answering
    // Terminal close (Completed/Irrelevent): the doubt is resolved, so drop any
    // "waiting on faculty" state locally too — otherwise a pending student reply
    // (e.g. "thank you") would keep it in the not-solved view until the refetch.
    const terminal = status === DOUBT_STATUS.SOLVED || status === DOUBT_STATUS.IRRELEVANT;

    // Persist the answer + status to the backend (doubts_tracking / doubts_app).
    const saved = this.saveDoubtAnswerToBackend(
      this.state.doubts.find((d) => d.id === id),
      status,
      text,
      image,
      answerer
    );

    // Clear the reply box, stay on this doubt, and reflect the new status + the claim.
    this.setState((prev) => ({
      replyText: "",
      answerImage: "",
      replyStatus: "",
      doubts: prev.doubts.map((d) => {
        if (d.id !== id) return d;
        const hasAnswer = !!(text || image);
        return {
          ...d,
          status: status || d.status,
          // A terminal close settles the doubt even with a student message last.
          awaitingReply: terminal ? false : d.awaitingReply,
          pendingCount: terminal ? 0 : d.pendingCount,
          // Claim: assign to the answering faculty so it leaves the shared pool.
          assignedFaculty: claiming && answerer.id ? answerer.name : d.assignedFaculty,
          assignedFacultyId: claiming && answerer.id ? answerer.id : d.assignedFacultyId,
          answer: hasAnswer
            ? { text, image, answeredBy: answerer.name || "Faculty", answeredAt: new Date().toISOString() }
            : d.answer,
        };
      }),
    }));

    // Once the reply row is written, reload the thread so the new bubble appears.
    if (text || image) {
      Promise.resolve(saved).then(() => {
        if (this.state.workloadDoubtId === id) this.fetchConversation(id);
      });
    }
  };

  // Delete a doubt from the list (mock data — removes it locally).
  // Open the in-app delete confirmation for a doubt.
  deleteDoubt = (id, e) => {
    if (e) e.stopPropagation();
    if (!this.canDeleteDoubts()) return;
    this.setState({ deleteConfirmId: id });
  };

  // Confirmed delete — remove from the view, then soft-delete on the backend.
  confirmDeleteDoubt = () => {
    const id = this.state.deleteConfirmId;
    if (!id || !this.canDeleteDoubts()) return;
    this.setState((prev) => ({
      deleteConfirmId: null,
      doubts: prev.doubts.filter((d) => d.id !== id),
      modalDoubtId: prev.modalDoubtId === id ? null : prev.modalDoubtId,
      chatDoubtId: prev.chatDoubtId === id ? null : prev.chatDoubtId,
      expandedId: prev.expandedId === id ? null : prev.expandedId,
    }));
    // Persist the delete to the backend (soft-delete: doubts_app.estatus = '0').
    if (this.props.client) {
      this.props.client
        .mutate({ mutation: DELETE_STUDENT_DOUBT, variables: { doubts_id: id } })
        .then(() => this.fetchDoubts())
        .catch((err) => {
          console.warn("[StudentDoubts] deleteStudentDoubt failed — restoring.", err);
          this.fetchDoubts();
        });
    }
  };

  // Small in-app confirmation dialog for deleting a doubt (replaces window.confirm).
  renderDeleteConfirm() {
    const id = this.state.deleteConfirmId;
    if (!id) return null;
    const d = this.state.doubts.find((x) => x.id === id);
    return (
      <Modal
        show
        onHide={() => this.setState({ deleteConfirmId: null })}
        centered
        dialogClassName="doubt-del-dialog"
      >
        {/* A bit wider than the default sm dialog. */}
        <style>{`.doubt-del-dialog{max-width:440px;}`}</style>
        {/* Top-right close (✕) */}
        <Modal.Header closeButton style={{ border: "none", padding: "10px 14px 0" }} />
        <Modal.Body style={{ textAlign: "center", padding: "8px 32px 34px" }}>
          <div
            style={{
              width: 60, height: 60, borderRadius: "50%", background: "#fee2e2", color: "#ef4444",
              display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", fontSize: 26,
            }}
          >
            <i className="fal fa-trash-alt" />
          </div>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>
            Delete this doubt?
          </div>
          <div style={{ fontSize: 13, color: "#64748b" }}>
            {d && d.title ? <strong>“{d.title}” </strong> : null}
            will be removed from the list. This can’t be undone.
          </div>
          <div className="d-flex" style={{ gap: 10, justifyContent: "center", marginTop: 22 }}>
            <Button variant="outline-secondary" onClick={() => this.setState({ deleteConfirmId: null })}>
              Cancel
            </Button>
            <Button variant="danger" onClick={this.confirmDeleteDoubt}>
              <i className="fal fa-trash-alt mr-1" />Delete
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  // Open a doubt as a chat conversation; pre-fill the reply box with any
  // existing answer so staff can review or refine it.
  selectChatDoubt = (d) => {
    const ans = d.answer || {};
    this.setState({ chatDoubtId: d.id, replyText: ans.text || "", answerImage: ans.image || "" });
  };

  // Staff attaches an image to their answer (read as a data URL).
  handleAnswerImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => this.setState({ answerImage: reader.result });
    reader.readAsDataURL(file);
  };

  markReviewing = (id, e) => {
    if (e) e.stopPropagation();
    if (!this.canEditDoubts()) return;
    this.setState((prev) => ({
      doubts: prev.doubts.map((d) =>
        d.id === id && d.status !== DOUBT_STATUS.SOLVED
          ? { ...d, status: DOUBT_STATUS.REVIEWING }
          : d
      ),
    }));
  };

  // Re-assign a doubt to a different staff member. The mobile app auto-tags a
  // faculty from the subject the student picked, but a wrong subject sends the
  // doubt to the wrong staff — this lets the tagged staff forward it to the
  // correct person.
  // Reassign the doubt's FACULTY (subject is correct). Persists assigned_faculty_id
  // while keeping the current subject. The doubt stays visible in the right pane
  // (it just leaves the current faculty's left-list).
  reassignFaculty = (id, faculty) => {
    if (!faculty || !this.canEditDoubts()) return;
    const doubt = this.state.doubts.find((d) => d.id === id);
    const fac = this.state.masterFaculty.find((f) => f.name === faculty);
    const facultyId = fac ? fac.id : null;
    this.setState((prev) => ({
      doubts: prev.doubts.map((d) =>
        d.id === id ? { ...d, assignedFaculty: faculty, assignedFacultyId: facultyId } : d
      ),
    }));
    if (this.props.client && facultyId) {
      this.props.client
        .mutate({
          mutation: REASSIGN_DOUBT,
          // Keep the current subject; only change the faculty.
          variables: { doubts_id: id, subject_id: doubt ? doubt.subjectId : null, faculty_id: facultyId },
        })
        // Re-pull so the doubt actually MOVES to the chosen faculty: the re-scope
        // hands it to that faculty (and drops it from a faculty-login's own view).
        .then(() => this.fetchDoubts())
        .catch((err) => console.warn("[StudentDoubts] reassignDoubt (faculty) failed.", err));
    }
  };

  // Subjects offered at the logged-in institute (institution_id based), in the
  // neetjee id-space that doubts use. Built from the institute-scoped masters —
  // the class_subjects curriculum (education_mngt.class_subjects) ∪ the subjects
  // the institute's faculty teach — then filtered against the neetjee subject
  // master so the reassign mutation still receives a valid neetjee subject id.
  // Both sources are fetched with institution_id, so the list is institute-scoped
  // rather than the full global taxonomy. Falls back to the full master list only
  // until those institute masters have loaded.
  instituteSubjects = () => {
    const { masterSubjects, masterClassSubjects, masterFaculty } = this.state;
    const ids = new Set();
    masterClassSubjects.forEach((cs) =>
      String(cs.subject_ids || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean)
        .forEach((sid) => ids.add(String(sid)))
    );
    masterFaculty.forEach((f) => (f.subjectIds || []).forEach((sid) => ids.add(String(sid))));
    if (ids.size === 0) return masterSubjects;
    const scoped = masterSubjects.filter((s) => ids.has(String(s.id)));
    return scoped.length > 0 ? scoped : masterSubjects;
  };

  // Change a doubt's SUBJECT (student picked the wrong subject). Clears the faculty
  // so the faculty dropdown then offers ONLY the new subject's teachers; the staff
  // then picks one. Persists the subject and clears assigned_faculty_id.
  reassignWorkloadSubject = (id, subjectName) => {
    if (!this.canEditDoubts()) return;
    const subj = this.state.masterSubjects.find((s) => s.subject === subjectName);
    if (!subj) return;
    this.setState((prev) => ({
      doubts: prev.doubts.map((d) =>
        d.id === id
          ? { ...d, subject: subjectName, subjectId: subj.id, assignedFaculty: "Unassigned", assignedFacultyId: null }
          : d
      ),
    }));
    if (this.props.client) {
      this.props.client
        .mutate({ mutation: REASSIGN_DOUBT, variables: { doubts_id: id, subject_id: subj.id, faculty_id: null } })
        // Re-pull so the subject change re-routes the doubt to the new subject's
        // faculty and the move is reflected.
        .then(() => this.fetchDoubts())
        .catch((err) => console.warn("[StudentDoubts] reassignDoubt (subject) failed.", err));
    }
  };

  // Atomic reassign — apply a new SUBJECT and/or FACULTY together in one action
  // (so a subject change + faculty pick persist as one move, and the doubt doesn't
  // vanish from a faculty's view mid-flow). subjectName defaults to the current
  // subject when only the faculty changes.
  applyReassign = (id, subjectName, facultyName) => {
    const subj = this.state.masterSubjects.find((s) => s.subject === subjectName);
    const fac = this.state.masterFaculty.find((f) => f.name === facultyName);
    const subjectId = subj ? subj.id : null;
    const facultyId = fac ? fac.id : null;
    this.setState((prev) => ({
      reassignDoubtId: null,
      reassignSubject: "",
      reassignFaculty: "",
      doubts: prev.doubts.map((d) =>
        d.id === id
          ? {
              ...d,
              subject: subjectName || d.subject,
              subjectId: subjectId != null ? subjectId : d.subjectId,
              assignedFaculty: facultyName || "Unassigned",
              assignedFacultyId: facultyId,
            }
          : d
      ),
    }));
    if (this.props.client) {
      this.props.client
        .mutate({
          mutation: REASSIGN_DOUBT,
          variables: { doubts_id: id, subject_id: subjectId, faculty_id: facultyId },
        })
        // Re-pull so the reassignment (subject + faculty) is reflected / re-scoped.
        .then(() => this.fetchDoubts())
        .catch((err) => console.warn("[StudentDoubts] applyReassign failed.", err));
    }
  };

  submitAnswer = (id, e) => {
    if (e) e.stopPropagation();
    const text = this.state.replyText.trim();
    const image = this.state.answerImage;
    // An answer can be text, image, or both — but not empty.
    if (!text && !image) return;
    this.setState((prev) => ({
      replyText: "",
      answerImage: "",
      expandedId: null,
      modalDoubtId: null,
      doubts: prev.doubts.map((d) =>
        d.id === id
          ? {
              ...d,
              status: DOUBT_STATUS.SOLVED,
              answer: {
                text,
                image,
                answeredBy: "Faculty",
                answeredAt: new Date().toISOString(),
              },
            }
          : d
      ),
    }));
  };

  // Student attaches a photo of their doubt — read as a data URL so it displays.
  handleNewImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () =>
      this.setState((prev) => ({ newDoubt: { ...prev.newDoubt, image: reader.result } }));
    reader.readAsDataURL(file);
  };

  submitNewDoubt = () => {
    const { newDoubt, nextId } = this.state;
    if (!newDoubt.studentName.trim() || !newDoubt.title.trim()) return;
    const doubt = {
      id: nextId,
      studentName: newDoubt.studentName.trim(),
      rollNo: "NEW",
      branch: newDoubt.branch,
      subject: newDoubt.subject,
      chapter: newDoubt.chapter,
      category: CATEGORIES[0],
      mobile: newDoubt.mobile.trim(),
      views: 0,
      isPublic: false,
      title: newDoubt.title.trim(),
      description: newDoubt.title.trim(),
      images: newDoubt.image ? [newDoubt.image] : [],
      status: DOUBT_STATUS.PENDING,
      // Use the chosen faculty; fall back to subject_ids auto-routing if left blank.
      assignedFaculty: newDoubt.faculty || this.facultyForSubject(newDoubt.subject) || "Unassigned",
      createdAt: new Date().toISOString(),
      answer: null,
    };
    // Add to the raw list too, so a later applyScope() (e.g. when the branches
    // reload) doesn't drop the row for having no backing record.
    this.setState((prev) => ({
      allDoubts: [doubt, ...prev.allDoubts],
      doubts: [doubt, ...prev.doubts],
      nextId: prev.nextId + 1,
      showSubmit: false,
      activeTab: "All",
      newDoubt: { studentName: "", mobile: "", subject: "", chapter: "", branch: "", faculty: "", title: "", image: "" },
    }));
  };

  resetFilters = () => {
    // Clear then re-apply the all-selected defaults so Reset returns every
    // dropdown to "all checked" (not to empty/unchecked).
    this.setState(
      {
        activeTab: "All",
        searchText: "",
        fBranch: [],
        fClass: [],
        fSection: [],
        fSubject: [],
        fChapter: [],
        fFaculty: [],
        fDateFrom: "",
        fDateTo: "",
        openDropdown: null,
        bcsInit: false,
        subjInit: false,
        chapInit: false,
        facInit: false,
      },
      () => {
        this.initBranchDefaults();
        this.initMasterDefaults();
      }
    );
  };

  // A dropdown only restricts results when the user has narrowed it — some but
  // not all options selected. Empty or all-selected both mean "no filter".
  filterNarrows = (selected, options) =>
    selected.length > 0 && selected.length < options.length;

  // ── Cascade handlers ────────────────────────────────────────────────────────
  // When a parent dropdown changes, refresh its dependent dropdown(s) to all of
  // the parent's options (all-checked) instead of leaving them emptied/unchecked
  // — so the child dropdown re-scopes and stays visibly selected.

  // For a set of selected class ids, the class-narrowed Subject selection (all
  // checked) and the matching Faculty selection. Used so Class → Subject → Faculty
  // stay consistent as the class filter changes.
  scopeSubjectFacultyToClasses = (classIds) => {
    const subjectObjs = this.allowedSubjectsForClasses(classIds);
    const fSubject = subjectObjs.map((s) => s.subject);
    const selIds = subjectObjs.map((s) => String(s.id));
    const fChapter = this.state.masterChapters
      .filter((c) => selIds.includes(String(c.subject)))
      .map((c) => c.chapter);
    // visibleFaculty(), not masterFaculty: a scoped employee's faculty selection is
    // only ever themselves, so the cascade must not re-select the whole staff list.
    const fFaculty = this.visibleFaculty()
      .filter((f) => f.subjectIds.some((sid) => selIds.includes(String(sid))))
      .map((f) => f.name);
    return { fSubject, fChapter, fFaculty };
  };

  onBranchChange = (val) => {
    const { branches, allClasses } = this.state;
    const scope = getUserScope();
    const unscoped = isUnscopedUser();
    const mySections = unscoped ? null : allowedSectionIds(branches, scope);
    const selB = branches.filter((b) => val.includes(String(b.id)));
    // Cascade only within the assignment: picking a branch may never select a class
    // or section this user isn't assigned to.
    const secs = selB
      .flatMap((b) => b.sections || [])
      .filter((s) => !mySections || idInScope(mySections, s.id));
    // An assigned employee's class selection is their assignment — not whatever the
    // branch's section rows happen to mention (which can be nothing at all).
    const fClass =
      !unscoped && scope.classIds.length > 0
        ? allClasses.filter((c) => idInScope(scope.classIds, c.id)).map((c) => String(c.id))
        : [...new Set(secs.map((s) => String(s.class_id)))].filter((cid) =>
            allClasses.some((c) => String(c.id) === cid)
          );
    const fSection = secs.map((s) => String(s.id));
    this.setState({ fBranch: val, fClass, fSection, ...this.scopeSubjectFacultyToClasses(fClass) });
  };

  onClassChange = (val) => {
    const { branches, fBranch } = this.state;
    const scope = getUserScope();
    const unscoped = isUnscopedUser();
    const mySections = unscoped ? null : allowedSectionIds(branches, scope);
    const inScopeBranches = unscoped
      ? branches
      : branches.filter((b) => idInScope(scope.branchIds, b.id));
    const selB =
      fBranch.length > 0
        ? inScopeBranches.filter((b) => fBranch.includes(String(b.id)))
        : inScopeBranches;
    const fSection = selB
      .flatMap((b) => b.sections || [])
      .filter((s) => val.length === 0 || val.includes(String(s.class_id)))
      .filter((s) => !mySections || idInScope(mySections, s.id))
      .map((s) => String(s.id));
    this.setState({ fClass: val, fSection, ...this.scopeSubjectFacultyToClasses(val) });
  };

  // When the Subject filter changes, re-scope the Chapter and Faculty dropdowns to
  // the selected subjects (all-checked): chapters via chapter.subject, faculty via
  // employee.subject_ids.
  onSubjectChange = (val) => {
    const selIds = this.state.masterSubjects
      .filter((s) => val.includes(s.subject))
      .map((s) => String(s.id));
    const fChapter = this.state.masterChapters
      .filter((c) => selIds.includes(String(c.subject)))
      .map((c) => c.chapter);
    const fFaculty = this.visibleFaculty()
      .filter((f) => val.length === 0 || f.subjectIds.some((sid) => selIds.includes(String(sid))))
      .map((f) => f.name);
    this.setState({ fSubject: val, fChapter, fFaculty });
  };

  // Auto-route a doubt: the faculty whose education_mngt.employee.subject_ids
  // include the doubt's subject. Mirrors the Assignment module's lecturer-by-
  // subject lookup. Returns "" when no staff teaches that subject.
  facultyForSubject = (subjectName) => {
    const list = this.facultyListForSubject(subjectName);
    return list.length > 0 ? list[0].name : "";
  };

  // Every faculty who teaches the given subject. Matches by subject NAME first
  // (a faculty's education_mngt subject_names, from getDoubtFaculty) and falls back
  // to the neetjee subject id — the SAME robust basis isMyDoubt uses. Matching by
  // id alone breaks when the education_mngt subject_ids and the neetjee subject ids
  // diverge: a doubt then passes the dashboard's name check (so it shows there) but
  // fails an id-only teacher lookup, so it never appears in the Assignment /
  // Faculty Workload tab. Name-first matching keeps both tabs in agreement.
  facultyListForSubject = (subjectName) => {
    const norm = (s) => String(s || "").trim().toLowerCase();
    const target = norm(subjectName);
    if (!target) return [];
    const subj = this.state.masterSubjects.find((s) => norm(s.subject) === target);
    const subjId = subj ? String(subj.id) : null;
    return this.state.masterFaculty.filter(
      (f) =>
        (f.subjectNames || []).some((n) => norm(n) === target) ||
        (subjId != null && f.subjectIds.map(String).includes(subjId))
    );
  };

  // Is the logged-in user a MAIN-COLLEGE / management login? Those see every doubt
  // across all subjects. Uses the app-wide convention: userlevel (employee_type_id)
  // "1" or "2" = institution admin / management (the same check every other module
  // uses — e.g. `if (userlevel !== "1" && userlevel !== "2") { restrict }`), while
  // teaching faculty are employee_type_id 3. An explicit management login
  // (RoleLogin → loginRole "management") also qualifies.
  isMainCollege = () => {
    const role = String(Cookies.get("loginRole") || "").toLowerCase();
    if (role === "management") return true;
    const lvl = String(Cookies.get("userlevel") || "");
    return lvl === "1" || lvl === "2";
  };

  // Is this doubt visible to the logged-in user?
  //  • Main college (management) → sees EVERY doubt.
  //  • Subject faculty → every UNCLAIMED doubt whose subject they teach (the SHARED
  //    POOL: all teachers of that subject see it, in each of their own logins),
  //    plus any doubt already claimed by / assigned to them.
  //  • Any login with no subjects mapped (admin / non-teacher) → sees all.
  //
  // CLAIM-ON-REPLY: the moment one teacher replies, the doubt is assigned to that
  // teacher (assignedFacultyId) and drops out of every OTHER teacher's view — only
  // the one who answered keeps it. That is what removes a Botany doubt from the two
  // teachers who didn't answer once the third one replies.
  // Is this doubt visible to the logged-in user? Access is the ASSIGNMENT SCOPE and
  // nothing else: the doubt's Branch / Class / Section / Subject must match the ids
  // this employee is assigned (education_mngt.employee). A dimension they were never
  // assigned (empty list) doesn't restrict them. Sections aren't assignable, so they
  // are derived Branch → Class → Section.
  //
  // Deliberately NOT gated on assigned_faculty_id. Doubts are a shared pool (see
  // facultyOwnsDoubt): every teacher of the subject sees them and whoever replies
  // first handles it. Hiding the ones another teacher has already been tagged with
  // would empty the dashboard even though the records are squarely in this user's
  // branch, class and subject.
  //
  // Institute admins / management (userlevel 1 & 2) are unscoped and see everything.
  isMyDoubt = (d) => {
    if (this.isMainCollege()) return true; // management login → all doubts
    const myId = parseInt(Cookies.get("id")) || null;
    const { ids, names } = this.mySubjectScope();
    // ADMIN / NON-TEACHING: a login with no RESOLVABLE subject sees every doubt.
    // We key off resolved subject NAMES so a stray/placeholder subject_ids that
    // maps to no real subject still counts as "no subjects" — not a subject
    // teacher, so it sees everything.
    if (names.length === 0) return true;
    // CLAIMED: once a faculty replies, the doubt is assigned to them and REMOVED
    // from every other faculty's view — only the owner keeps seeing it.
    if (d.assignedFacultyId) return d.assignedFacultyId === myId;
    // UNCLAIMED (shared pool): visible to every teacher of the doubt's subject.
    // Match by subject NAME first (case/space-insensitive, immune to id-space
    // differences), then by subject id as a fallback.
    const norm = (s) => String(s || "").trim().toLowerCase();
    if (d.subject && names.some((n) => norm(n) === norm(d.subject))) return true;
    return d.subjectId != null && ids.includes(String(d.subjectId));
    if (isUnscopedUser()) return true;

    const scope = getUserScope();
    if (!idInScope(scope.branchIds, d.branchId)) return false;
    if (!idInScope(scope.classIds, d.classId)) return false;
    if (!idInScope(scope.subjectIds, d.subjectId)) return false;
    const mySections = allowedSectionIds(this.state.branches, scope);
    if (mySections && !idInScope(mySections, d.sectionId)) return false;
    return true;
  };

  // The faculty this user may see anywhere in the module.
  //
  // If the logged-in employee IS one of the teaching faculty — matched on the employee
  // id cookie, falling back to their name when the login id and the employee id don't
  // line up — they see only THEMSELVES. If they don't match any faculty row (an admin,
  // or a non-teaching role), the list isn't about them, so show ALL faculty rather than
  // an empty dropdown.
  visibleFaculty = () => {
    const { masterFaculty } = this.state;
    if (isUnscopedUser()) return masterFaculty;

    const myId = Cookies.get("id");
    const byId = masterFaculty.filter((f) => String(f.id) === String(myId));
    if (byId.length > 0) return byId;

    const myName = String(Cookies.get("name") || "").trim().toLowerCase();
    const byName = myName
      ? masterFaculty.filter((f) => String(f.name || "").trim().toLowerCase() === myName)
      : [];
    if (byName.length > 0) return byName;

    // No match → this login isn't a teaching faculty; don't hide the list from them.
    return masterFaculty;
  };

  // Does faculty `facultyName` own this doubt? A manual reassignment / a claim
  // (assigned_faculty_id) gives it to that one faculty; otherwise it's a SHARED
  // POOL — every teacher of the doubt's subject sees it (whoever answers first
  // claims it and it leaves the others). Doubts with no subject-teacher and no
  // manual assignment fall to "Unassigned".
  facultyOwnsDoubt = (facultyName, d) => {
    if (d.assignedFacultyId) {
      const fac = this.state.masterFaculty.find((x) => x.id === d.assignedFacultyId);
      return fac ? fac.name === facultyName : facultyName === "Unassigned";
    }
    const teachers = this.facultyListForSubject(d.subject);
    if (teachers.length === 0) return facultyName === "Unassigned";
    return teachers.some((x) => x.name === facultyName);
  };

  anyFilterActive = () => {
    const { activeTab, searchText, fBranch, fClass, fSection, fSubject, fChapter, fFaculty, fDateFrom, fDateTo } = this.state;
    const { branchOptions, classOptions, sectionOptions, subjectOptions, chapterOptions, facultyOptions } =
      this.getFilterOptions();
    return (
      activeTab !== "All" ||
      !!searchText ||
      !!fDateFrom ||
      !!fDateTo ||
      this.filterNarrows(fBranch, branchOptions) ||
      this.filterNarrows(fClass, classOptions) ||
      this.filterNarrows(fSection, sectionOptions) ||
      this.filterNarrows(fSubject, subjectOptions) ||
      this.filterNarrows(fChapter, chapterOptions) ||
      this.filterNarrows(fFaculty, facultyOptions)
    );
  };

  // Effective status used for counts/filters/badges: a Completed doubt the student
  // re-asked counts as Reviewing again (it's no longer "completed").
  effectiveStatus = (d) =>
    d.status === DOUBT_STATUS.SOLVED && d.awaitingReply ? DOUBT_STATUS.REVIEWING : d.status;

  countByStatus = (status) =>
    status === "All"
      ? this.state.doubts.length
      : this.state.doubts.filter((d) => this.effectiveStatus(d) === status).length;

  // Apply callback from the Expense-style DateBlock range picker. Both args are
  // moment objects; store the range as YYYY-MM-DD strings that getVisible filters on.
  handleDateBlockCallback = (start, end) => {
    this.setState({
      fDateFrom: start.format("YYYY-MM-DD"),
      fDateTo: end.format("YYYY-MM-DD"),
      page: 1,
    });
  };

  // Stable key that identifies one student across their doubts (name + mobile).
  studentKey = (d) =>
    `${String(d.studentName || "").trim().toLowerCase()}|${String(d.mobile || "").trim()}`;

  // Collapse a doubt list into ONE record per student, with per-student counts,
  // so the dashboard shows unique students (not one row per doubt). Sorted by the
  // most recent doubt first.
  groupByStudent = (list) => {
    const map = new Map();
    list.forEach((d) => {
      const key = this.studentKey(d);
      let g = map.get(key);
      if (!g) {
        g = {
          key, name: d.studentName || "Student", mobile: d.mobile || "",
          admissionNo: d.admissionNo || "",
          class: d.class || "", section: d.section || "", branch: d.branch || "",
          total: 0, notSolved: 0, latestMs: 0, latestAt: "",
        };
        map.set(key, g);
      }
      g.total += 1;
      if (this.effectiveStatus(d) !== DOUBT_STATUS.SOLVED) g.notSolved += 1;
      const t = this.doubtMs(d.createdAt) || 0;
      if (t >= g.latestMs) { g.latestMs = t; g.latestAt = d.createdAt; }
    });
    return [...map.values()].sort((a, b) => b.latestMs - a.latestMs);
  };

  // Parse a doubt timestamp (epoch seconds/ms or ISO string) to milliseconds.
  doubtMs = (val) => {
    if (!val) return null;
    const s = String(val).trim();
    const ms = /^\d+$/.test(s) ? (s.length <= 10 ? Number(s) * 1000 : Number(s)) : new Date(s).getTime();
    return ms && !isNaN(ms) ? ms : null;
  };

  getVisible = () => {
    const {
      doubts, activeTab, searchText, fBranch, fClass, fSection, fSubject, fChapter, fFaculty,
      fDateFrom, fDateTo, branches, allClasses,
    } = this.state;
    const q = searchText.trim().toLowerCase();
    // Date bounds (whole days, inclusive) from the From/To pickers.
    const fromMs = fDateFrom ? new Date(`${fDateFrom}T00:00:00`).getTime() : null;
    const toMs = fDateTo ? new Date(`${fDateTo}T23:59:59.999`).getTime() : null;

    // A filter only restricts results when narrowed (some but not all selected).
    // So the default "all selected" state — and a deselect-all — both show every
    // doubt, including ones with a null branch / no class / unassigned faculty.
    const { branchOptions, classOptions, sectionOptions, subjectOptions, chapterOptions, facultyOptions } =
      this.getFilterOptions();
    const n = this.filterNarrows;

    // The filter dropdowns store backend IDs; the doubt records carry
    // human-readable branch/class/section names. Resolve the selected IDs to
    // names so the two line up.
    const branchNames = new Set(
      fBranch.map((id) => (branches.find((b) => String(b.id) === String(id)) || {}).branch_name)
    );
    const classNames = new Set(
      fClass.map((id) => (allClasses.find((c) => String(c.id) === String(id)) || {}).class)
    );
    const allSections = branches.flatMap((b) => b.sections || []);
    const sectionNames = new Set(
      fSection.map((id) => (allSections.find((s) => String(s.id) === String(id)) || {}).section_name)
    );

    return doubts.filter((d) => {
      if (activeTab !== "All" && this.effectiveStatus(d) !== activeTab) return false;
      // Date-range filter on the doubt's raised date.
      if (fromMs != null || toMs != null) {
        const t = this.doubtMs(d.createdAt);
        if (t == null) return false;
        if (fromMs != null && t < fromMs) return false;
        if (toMs != null && t > toMs) return false;
      }
      if (n(fBranch, branchOptions) && !branchNames.has(d.branch)) return false;
      if (n(fClass, classOptions) && !classNames.has(d.class)) return false;
      if (n(fSection, sectionOptions) && !sectionNames.has(d.section)) return false;
      if (n(fSubject, subjectOptions) && !fSubject.includes(d.subject)) return false;
      if (n(fChapter, chapterOptions) && !fChapter.includes(d.chapter)) return false;
      // Shared pool: a doubt matches a faculty filter if that faculty owns it
      // (manually assigned, or teaches the doubt's subject).
      if (n(fFaculty, facultyOptions) && !fFaculty.some((name) => this.facultyOwnsDoubt(name, d))) return false;
      if (q) {
        const hay = `${d.title} ${d.description} ${d.studentName} ${d.rollNo}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  };

  // Shared answer-image picker + preview (used by the inline card and the modal).
  renderAnswerImageField() {
    const { answerImage } = this.state;
    return (
      <div className="mt-2">
        <label
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#475569",
            display: "block",
            marginBottom: 4,
          }}
        >
          <i className="fal fa-paperclip mr-1"></i>
          {answerImage ? "Answer image attached ✓" : "Attach image to answer (optional)"}
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={this.handleAnswerImage}
          style={{ fontSize: 12 }}
        />
        {answerImage ? (
          <div className="mt-2" style={{ position: "relative", display: "inline-block" }}>
            <img
              src={answerImage}
              alt="answer preview"
              style={{
                maxHeight: 110,
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                display: "block",
              }}
            />
            <button
              type="button"
              onClick={() => this.setState({ answerImage: "" })}
              title="Remove image"
              style={{
                position: "absolute",
                top: -8,
                right: -8,
                width: 22,
                height: 22,
                borderRadius: "50%",
                border: "none",
                background: "#ef4444",
                color: "#fff",
                cursor: "pointer",
                fontSize: 12,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  // A single doubt card with expand-to-solve (reply + Reviewing / Submit).
  // Count badge showing the number of unanswered student messages.
  // Red  → doubt never answered yet (still pending a faculty answer).
  // Orange → doubt was answered, but the student asked again (re-ask).
  doubtCountBadge(d, size = 18) {
    const neverAnswered = !d.answer; // no faculty reply yet → pending an answer
    if (!(neverAnswered || d.awaitingReply) || d.pendingCount < 1) return null;
    return (
      <span
        style={{
          minWidth: size,
          height: size,
          borderRadius: size / 2,
          padding: "0 5px",
          background: neverAnswered ? "#ef4444" : "#f59e0b",
          color: "#fff",
          fontSize: 10,
          fontWeight: 700,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
        title={
          neverAnswered
            ? `${d.pendingCount} pending message — waiting for an answer`
            : `${d.pendingCount} new student reply — waiting for a response`
        }
      >
        {d.pendingCount}
      </span>
    );
  }

  renderDoubtCard(d) {
    const { expandedId, replyText, answerImage } = this.state;
    const open = expandedId === d.id;
    return (
      <div key={d.id} style={S.card} onClick={() => this.toggleExpand(d.id)}>
        {/* Card head */}
        <div className="d-flex align-items-start">
          <div style={{ ...S.avatar, background: avatarBg(d.status) }}>
            {initials(d.studentName)}
          </div>
          <div className="ml-2 flex-grow-1">
            <div className="d-flex justify-content-between align-items-start">
              <strong style={{ fontSize: 14, color: "#0f172a" }}>{d.title}</strong>
              <div className="d-flex align-items-center" style={{ gap: 6, flexWrap: "wrap" }}>
                {this.doubtCountBadge(d)}
                <span style={S.badge(this.effectiveStatus(d))}>
                  <span style={S.dot}></span>
                  {this.effectiveStatus(d)}
                </span>
              </div>
            </div>
            <div style={S.meta} className="mt-1">
              <i className="fal fa-user mr-1"></i>
              {d.studentName}
              <span className="mx-2">·</span>
              <i className="fal fa-code-branch mr-1"></i>
              {d.branch}
              <span className="mx-2">·</span>
              <i className="fal fa-book mr-1"></i>
              {d.subject}
              <span className="mx-2">·</span>
              <i className="fal fa-clock mr-1"></i>
              {timeAgo(d.createdAt)}
            </div>
            {d.images && d.images.length > 0 ? (
              <span style={S.imgTag}>
                <i className="fal fa-paperclip"></i>
                Image attached
              </span>
            ) : null}
          </div>
        </div>

        {/* Expanded body */}
        {open ? (
          <div className="mt-3" onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 14, color: "#334155" }}>{d.description}</div>

            {d.images && d.images.length > 0 ? (
              isImageSrc(d.images[0]) ? (
                <img
                  src={d.images[0]}
                  alt="doubt attachment"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 280,
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                    display: "block",
                    margin: "12px 0",
                  }}
                />
              ) : (
                <div style={S.imgBox}>
                  <i className="fal fa-image mr-1"></i>
                  {d.images[0]}
                </div>
              )
            ) : null}

            {d.answer ? (
              <div style={S.answerBox}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#15803d" }}>
                  <i className="fal fa-check-circle mr-1"></i>
                  Answered by {d.answer.answeredBy}
                </div>
                {d.answer.text ? (
                  <div style={{ fontSize: 13, color: "#334155", marginTop: 2 }}>
                    {d.answer.text}
                  </div>
                ) : null}
                {d.answer.image ? (
                  <img
                    src={d.answer.image}
                    alt="answer attachment"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 220,
                      borderRadius: 8,
                      border: "1px solid #bbf7d0",
                      display: "block",
                      marginTop: 8,
                    }}
                  />
                ) : null}
              </div>
            ) : null}

            <Form.Control
              as="textarea"
              rows={3}
              className="mt-3"
              placeholder="Type your answer (text, image, or both)..."
              value={replyText}
              onChange={(e) => this.setState({ replyText: e.target.value })}
            />
            {this.renderAnswerImageField()}
            <div className="d-flex mt-2" style={{ gap: 10 }}>
              <Button
                variant="outline-info"
                size="sm"
                className="flex-fill"
                disabled={d.status === DOUBT_STATUS.SOLVED}
                onClick={(e) => this.markReviewing(d.id, e)}
              >
                <i className="fal fa-eye mr-1"></i>Reviewing
              </Button>
              <Button
                variant="success"
                size="sm"
                className="flex-fill"
                onClick={(e) => this.submitAnswer(d.id, e)}
              >
                <i className="fal fa-check mr-1"></i>Submit answer
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  // A status-focused workspace tab (Pending / Review / Solved) — staff see
  // exactly the doubts in that state and solve them inline.
  renderStatusView(status, title, icon, color) {
    const items = this.state.doubts.filter((d) => this.effectiveStatus(d) === status);
    return (
      <div style={S.panel}>
        <div style={S.panelHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ ...S.hIcon, background: `${color}18`, color }}>
              <i className={icon} />
            </div>
            <span style={{ fontWeight: 700, fontSize: 13, color: "#1f2937" }}>{title}</span>
          </div>
          <span style={{ ...S.recordBadge, background: `${color}18`, color }}>
            {items.length} {items.length === 1 ? "doubt" : "doubts"}
          </span>
        </div>
        <div style={S.listWrap}>
          {items.length === 0 ? (
            <div className="text-center text-muted py-5">
              <i className="fal fa-check-circle fa-2x mb-2 d-block"></i>
              Nothing here — all caught up.
            </div>
          ) : (
            items.map((d) => this.renderDoubtCard(d))
          )}
        </div>
      </div>
    );
  }

  // Faculty Workload "View doubts" popup — list of all the faculty's doubts on
  // the left; click one to open its fields on the right and solve it. Stays open
  // so staff can solve several in a row before closing.
  // The two-pane doubts view (left = doubt list, right = selected doubt's reassign
  // bar + conversation). Shared by the faculty modal and the inline student view on
  // the Assignment tab. Reads the scope (student or faculty) from state.
  renderWorkloadPanes() {
    const studentKey = this.state.workloadStudentKey;
    const f = this.state.workloadFaculty;
    const scope = this.state.workloadScope;
    let ordered;
    if (studentKey) {
      ordered = this.state.doubts.filter((d) => this.studentKey(d) === studentKey);
    } else if (f) {
      // Faculty modal (legacy): the faculty's doubts in the chosen scope.
      ordered = this.state.doubts.filter(
        (d) => this.facultyOwnsDoubt(f, d) && this.inWorkloadScope(d, scope)
      );
    } else {
      // Assignment-tab inbox: ALL doubts.
      ordered = this.state.doubts;
    }
    // Header search applies to the inline views (student + all doubts), not the
    // faculty modal — matches title / description / subject / student name.
    const sq = f ? "" : (this.state.studentDoubtSearch || "").trim().toLowerCase();
    if (sq) {
      ordered = ordered.filter((d) =>
        `${d.title || ""} ${d.description || ""} ${d.subject || ""} ${d.studentName || ""}`
          .toLowerCase()
          .includes(sq)
      );
    }
    // Status filter from the inbox header (inline views only).
    if (!f) {
      if (this.state.assignFilter === "solved") {
        ordered = ordered.filter((d) => this.effectiveStatus(d) === DOUBT_STATUS.SOLVED);
      } else if (this.state.assignFilter === "unsolved") {
        ordered = ordered.filter((d) => this.effectiveStatus(d) !== DOUBT_STATUS.SOLVED);
      }
    }
    const active =
      this.state.doubts.find((d) => d.id === this.state.workloadDoubtId) || null;
    return (
          <div style={{ display: "flex", height: "100%", minHeight: 360 }}>
            {/* ── Left: doubt list ──────────────────────────────────────── */}
            <div
              style={{
                width: 300,
                flexShrink: 0,
                borderRight: "1px solid #e5e7eb",
                overflowY: "auto",
                background: "#fff",
              }}
            >
              {ordered.length === 0 ? (
                <div className="text-center text-muted py-5" style={{ fontSize: 13 }}>
                  No doubts.
                </div>
              ) : (
                ordered.map((d) => {
                  const selected = active && d.id === active.id;
                  return (
                    <div
                      key={d.id}
                      ref={selected ? this.activeDoubtRef : null}
                      onClick={() => this.selectWorkloadDoubt(d)}
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "flex-start",
                        padding: "11px 14px",
                        cursor: "pointer",
                        borderBottom: "1px solid #f5f5f5",
                        borderLeft: `3px solid ${selected ? PURPLE : "transparent"}`,
                        background: selected ? `${PURPLE}08` : "#fff",
                      }}
                    >
                      <div
                        style={{
                          width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                          background: avatarBg(d.status), color: "#0f172a",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 11, fontWeight: 700,
                        }}
                        title={`${d.studentName} · ${d.status}`}
                      >
                        {initials(d.studentName)}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontSize: 13, fontWeight: 600, color: "#0f172a",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}
                        >
                          {d.title}
                        </div>
                        <div style={{ marginTop: 4 }}>
                          {/* Highlighted subject tag */}
                          <span
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              background: `${PURPLE}14`, color: PURPLE, borderRadius: 6,
                              padding: "1px 8px", fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap",
                            }}
                          >
                            <i className="fal fa-book" />
                            {d.subject || "—"}
                          </span>
                        </div>
                      </div>
                      {this.doubtCountBadge(d) ||
                        (d.answer ? (
                          <i className="fal fa-check-circle" style={{ color: STATUS_COLOR[DOUBT_STATUS.SOLVED], fontSize: 13, marginTop: 3 }} />
                        ) : null)}
                    </div>
                  );
                })
              )}
            </div>

            {/* ── Right: selected doubt + solve fields ──────────────────── */}
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", padding: "2px 10px 8px", background: "#f8fafc" }}>
              {!active ? (
                <div
                  className="d-flex flex-column align-items-center justify-content-center text-muted"
                  style={{ height: "100%" }}
                >
                  <i className="fal fa-hand-pointer fa-2x mb-2" style={{ color: "#cbd5e1" }} />
                  <div style={{ fontSize: 14 }}>Select a doubt to solve</div>
                </div>
              ) : (
                <>
                  {/* Doubt header — identity + status. */}
                  <div className="d-flex justify-content-between align-items-start flex-wrap">
                    <strong style={{ fontSize: 15, color: "#0f172a" }}>{active.title}</strong>
                    <div className="d-flex align-items-center" style={{ gap: 8, flexWrap: "wrap" }}>
                      {this.doubtCountBadge(active, 20)}
                      {active.answer && active.awaitingReply ? (
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#b45309" }}>
                          New student reply — please respond
                        </span>
                      ) : null}
                      <span style={S.badge(this.effectiveStatus(active))}>
                        <span style={S.dot}></span>
                        {this.effectiveStatus(active)}
                      </span>
                    </div>
                  </div>
                  {active.chapter ? (
                    <div style={{ ...S.meta, marginTop: 4 }}>
                      <i className="fal fa-bookmark mr-1" />
                      {active.chapter}
                    </div>
                  ) : null}

                  {/* Reassign (subject / faculty) — only while the doubt is NOT
                      completed (once solved it stays with the faculty who solved it),
                      and only for a role granted is_edit: reassigning writes to the
                      doubt, so a view-only role doesn't get the dropdowns at all. */}
                  {active.status !== DOUBT_STATUS.SOLVED && this.canEditDoubts() ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      flexWrap: "wrap",
                      background: "#fffbeb",
                      border: "1px solid #fde68a",
                      borderRadius: 10,
                      padding: "8px 14px",
                      marginTop: 8,
                    }}
                  >
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#92400e", whiteSpace: "nowrap" }}>
                      <i className="fal fa-random mr-1" />
                      Wrong subject / faculty? Reassign:
                    </span>
                    {(() => {
                      // Pending selection is tied to THIS doubt; until the user
                      // touches a dropdown, it mirrors the doubt's current values.
                      const forThis = this.state.reassignDoubtId === active.id;
                      const selSubject = forThis
                        ? this.state.reassignSubject
                        : active.subject || "";
                      const selFaculty = forThis
                        ? this.state.reassignFaculty
                        : active.assignedFaculty && active.assignedFaculty !== "Unassigned"
                        ? active.assignedFaculty
                        : "";

                      // Subject options (institute-scoped) + keep the current one.
                      const subjectList = this.instituteSubjects();
                      const subjOpts =
                        selSubject && !subjectList.some((s) => s.subject === selSubject)
                          ? [{ id: `cur-${selSubject}`, subject: selSubject }, ...subjectList]
                          : subjectList;

                      // Faculty options are the teachers of the SELECTED subject.
                      const facList = this.facultyListForSubject(selSubject);
                      const facNames = facList.map((x) => x.name);
                      const facOpts =
                        selFaculty && !facNames.includes(selFaculty)
                          ? [{ id: selFaculty, name: selFaculty }, ...facList]
                          : facList;

                      const changed =
                        selSubject !== (active.subject || "") ||
                        selFaculty !==
                          (active.assignedFaculty && active.assignedFaculty !== "Unassigned"
                            ? active.assignedFaculty
                            : "");
                      const canReassign = changed && !!selFaculty;

                      return (
                        <>
                          {/* Subject — change it to move the doubt to another subject. */}
                          <Form.Control
                            as="select"
                            size="sm"
                            value={selSubject}
                            onChange={(e) =>
                              this.setState({
                                reassignDoubtId: active.id,
                                reassignSubject: e.target.value,
                                // Subject changed → the old faculty may not teach it.
                                reassignFaculty:
                                  e.target.value === (active.subject || "") ? selFaculty : "",
                              })
                            }
                            style={{ width: "auto", flex: 1, minWidth: 140 }}
                            title="Subject"
                          >
                            <option value="">Select subject…</option>
                            {subjOpts.map((s) => (
                              <option key={s.id}>{s.subject}</option>
                            ))}
                          </Form.Control>
                          {/* Faculty — teachers of the selected subject. */}
                          <Form.Control
                            as="select"
                            size="sm"
                            value={selFaculty}
                            disabled={facList.length === 0}
                            onChange={(e) =>
                              this.setState({
                                reassignDoubtId: active.id,
                                reassignSubject: selSubject,
                                reassignFaculty: e.target.value,
                              })
                            }
                            style={{ width: "auto", flex: 1, minWidth: 140 }}
                            title="Faculty"
                          >
                            <option value="">
                              {facList.length === 0 ? "No faculty for subject" : "Select staff…"}
                            </option>
                            {facOpts.map((fac) => (
                              <option key={fac.id || fac.name}>{fac.name}</option>
                            ))}
                          </Form.Control>
                          {/* Apply both (subject + faculty) in one move. */}
                          <button
                            type="button"
                            disabled={!canReassign}
                            onClick={() => this.applyReassign(active.id, selSubject, selFaculty)}
                            style={{
                              border: "none",
                              background: canReassign ? "#d97706" : "#e5e7eb",
                              color: "#fff",
                              borderRadius: 8,
                              padding: "6px 14px",
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: canReassign ? "pointer" : "default",
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                            }}
                          >
                            <i className="fal fa-random mr-1" />
                            Reassign
                          </button>
                        </>
                      );
                    })()}
                  </div>
                  ) : null}

                  {/* Conversation thread — fills remaining height, scrolls internally. */}
                  <div style={{ marginTop: 8, flex: 1, minHeight: 0, display: "flex" }}>
                    {this.renderConversationThread(active, () => this.submitWorkloadAnswer(active.id), true)}
                  </div>
                </>
              )}
            </div>
          </div>
    );
  }

  renderWorkloadModal() {
    const f = this.state.workloadFaculty;
    const studentKey = this.state.workloadStudentKey;
    // Student mode renders INLINE on the Assignment tab (see renderAssignments),
    // not as a modal overlay — so don't render the modal in that case.
    if (studentKey) return null;
    if (!f) return null;
    const scope = this.state.workloadScope; // "open" | "solved"
    const solvedScope = scope === "solved";
    // Student mode → ALL of one student's doubts (any status). Faculty mode → the
    // faculty's doubts in the chosen (solved / not-solved) scope.
    const ordered = studentKey
      ? this.state.doubts.filter((d) => this.studentKey(d) === studentKey)
      : this.state.doubts.filter(
          (d) => this.facultyOwnsDoubt(f, d) && this.inWorkloadScope(d, scope)
        );
    // Look the open doubt up from ALL doubts (not just this list) so it stays
    // visible in the right pane after a subject/faculty reassignment moves it out.
    const active =
      this.state.doubts.find((d) => d.id === this.state.workloadDoubtId) || null;
    // Header label / icon / count adapt to the mode.
    const headerName = studentKey ? (ordered[0] ? ordered[0].studentName : "Student") : f;

    return (
      <Modal show onHide={this.closeWorkloadModal} size="xl" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>
            <i className={`fal fa-${studentKey ? "user-graduate" : "chalkboard-teacher"} mr-2`} style={{ color: PURPLE }} />
            {headerName}
            <span
              style={{
                ...S.recordBadge,
                marginLeft: 10,
                fontSize: 12,
                background: studentKey ? `${PURPLE}18` : solvedScope ? `${STATUS_COLOR[DOUBT_STATUS.SOLVED]}18` : "#fef3c7",
                color: studentKey ? PURPLE : solvedScope ? STATUS_COLOR[DOUBT_STATUS.SOLVED] : "#b45309",
              }}
            >
              <i className={`fal fa-${studentKey ? "comments" : solvedScope ? "check-circle" : "clock"} mr-1`} />
              {ordered.length}{" "}
              {studentKey
                ? ordered.length === 1 ? "doubt" : "doubts"
                : solvedScope ? "solved" : "not solved"}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 10, overflow: "hidden" }}>
          {/* The Modal is `scrollable`, so the dialog is capped to the viewport and
              the header/footer stay fixed. The body fills that space and only the
              inner panes (left list + conversation) scroll — the modal never does. */}
          {this.renderWorkloadPanes()}
        </Modal.Body>
      </Modal>
    );
  }

  // Conversation thread for the Answer modal. The original doubt (doubts_app) is
  // the first message on the student side; every doubts_tracking row follows in
  // time order — the student's follow-ups stay on the left, faculty replies move
  // to the right — so a doubt that's asked again by the same student reads as one
  // continuous WhatsApp-style conversation, keyed by doubt id + doubts_user_type.
  // Save / Cancel buttons shown under an inline message editor.
  renderEditControls(onSave) {
    return (
      <div className="d-flex" style={{ gap: 6, marginTop: 6, justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={this.cancelEdit}
          style={{ border: "1px solid #cbd5e1", background: "#fff", color: "#475569", borderRadius: 6, fontSize: 11, fontWeight: 600, padding: "3px 10px", cursor: "pointer" }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          style={{ border: "none", background: "#22c55e", color: "#fff", borderRadius: 6, fontSize: 11, fontWeight: 600, padding: "3px 12px", cursor: "pointer" }}
        >
          <i className="fal fa-check mr-1" />Save
        </button>
      </div>
    );
  }

  renderConversationThread(d, onSend, fill = false) {
    const { modalConversation, modalConvLoading, replyText, answerImage } = this.state;
    // Selecting a status is MANDATORY: you can't send an answer (or a status-only
    // update) without choosing a status. `needStatus` = an answer is typed but no
    // status is picked yet — used to flag the dropdown.
    const hasAnswer = !!(replyText.trim() || answerImage);
    const needStatus = hasAnswer && !this.state.replyStatus;

    // WhatsApp-style read receipt (blue ✓✓). The student mobile app has no true
    // read flag, so we infer "seen" from the student engaging AFTER a faculty
    // reply: id of the newest student message in the thread. Any faculty message
    // older than it was necessarily on screen when the student wrote back. A
    // like/rating on the reply itself is also proof the student saw it.
    const lastStudentMsgId = modalConversation.reduce(
      (max, msg) => (this.isFacultyMessage(msg) ? max : Math.max(max, Number(msg.id) || 0)),
      0
    );
    // Posting a reply, attaching an image and setting a status all WRITE to the
    // doubt, so the whole composer is an is_edit action: a view-only role reads the
    // thread but gets no way to add to it. Editing/deleting a message follow their
    // own grants.
    const canEditThread = this.canEditDoubts();
    const canDeleteThread = this.canDeleteDoubts();
    const canSend = canEditThread && !!this.state.replyStatus;

    // Wrap long text and break unbroken strings (e.g. "ppppp…") so they never
    // overflow the bubble.
    const wrap = { wordBreak: "break-word", overflowWrap: "anywhere", minWidth: 0 };
    const studentBubble = {
      maxWidth: "78%",
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: "2px 12px 12px 12px",
      padding: "9px 12px",
      boxShadow: "0 1px 1px rgba(0,0,0,0.05)",
      ...wrap,
    };
    const facultyBubble = {
      maxWidth: "78%",
      background: "#d9fdd3",
      border: "1px solid #bbf7d0",
      borderRadius: "12px 2px 12px 12px",
      padding: "9px 12px",
      boxShadow: "0 1px 1px rgba(0,0,0,0.05)",
      ...wrap,
    };
    const metaLine = (color) => ({ fontSize: 10, color, marginTop: 4, textAlign: "right" });
    const bubbleImg = {
      marginTop: 8,
      maxWidth: "100%",
      maxHeight: 240,
      borderRadius: 8,
      display: "block",
    };

    return (
      <div
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: 10,
          overflow: "hidden",
          // Always a flex column so the composer stays pinned at the bottom and only
          // the messages area scrolls. fill = stretch to the parent's height (modal
          // pane); otherwise bound the block so it's tall but never overflows.
          display: "flex",
          flexDirection: "column",
          ...(fill
            ? { flex: 1, minHeight: 0, width: "100%" }
            : { minHeight: 480, maxHeight: "70vh" }),
        }}
      >
      {/* Messages — the only scrolling part */}
      <div
        style={{
          background: "#efeae2",
          padding: "14px 14px 6px",
          overflowY: "auto",
          flex: 1,
          minHeight: 0,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", marginBottom: 10 }}>
          <i className="fal fa-comments mr-1"></i>Conversation
        </div>

        {/* Original doubt (doubts_app) — first message, student side */}
        <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 12 }}>
          <div style={studentBubble}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#3730a3", marginBottom: 2 }}>
              {d.studentName}
            </div>
            {this.state.editingKey === "q" ? (
              <>
                <input
                  type="text"
                  value={this.state.editTitle}
                  onChange={(e) => this.setState({ editTitle: e.target.value })}
                  placeholder="Title"
                  style={{ width: "100%", fontSize: 13, fontWeight: 600, marginBottom: 4, border: "1px solid #cbd5e1", borderRadius: 6, padding: "4px 6px" }}
                />
                <textarea
                  value={this.state.editText}
                  onChange={(e) => this.setState({ editText: e.target.value })}
                  rows={3}
                  placeholder="Description"
                  style={{ width: "100%", fontSize: 13, border: "1px solid #cbd5e1", borderRadius: 6, padding: "4px 6px" }}
                />
                {this.renderEditControls(() => this.saveEditQuestion(d))}
              </>
            ) : (
              <>
                {d.title ? (
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", marginBottom: 2 }}>
                    {d.title}
                  </div>
                ) : null}
                {d.description ? (
                  <div style={{ fontSize: 13, color: "#334155" }}>{d.description}</div>
                ) : null}
                {d.images && d.images[0] && isImageSrc(d.images[0]) ? (
                  <img src={d.images[0]} alt="doubt" style={bubbleImg} />
                ) : null}
                <div style={metaLine("#94a3b8")}>{formatDateTime(d.createdAt)}</div>
              </>
            )}
          </div>
        </div>

        {/* Follow-ups & replies (doubts_tracking), oldest → newest */}
        {modalConversation.map((m) => {
          const faculty = this.isFacultyMessage(m);
          const who = m.name || (faculty ? "Faculty" : d.studentName);
          // Faculty reply is "seen" once the student engages after it: a newer
          // student message, or a like/rating on this very reply.
          const seenByStudent =
            faculty &&
            (m.is_liked === "1" ||
              (m.rating && Number(m.rating) > 0) ||
              (lastStudentMsgId > 0 && Number(m.id) < lastStudentMsgId));
          return (
            <div
              key={m.id}
              style={{
                display: "flex",
                justifyContent: faculty ? "flex-end" : "flex-start",
                marginBottom: 12,
              }}
            >
              <div style={faculty ? facultyBubble : studentBubble}>
                <div className="d-flex justify-content-between align-items-start" style={{ gap: 8 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: faculty ? "#15803d" : "#3730a3",
                      marginBottom: 2,
                    }}
                  >
                    {who}
                    {m.user_type ? (
                      <span style={{ fontWeight: 400, color: "#94a3b8" }}> · {m.user_type}</span>
                    ) : null}
                  </div>
                  {/* Per-message actions, gated the same way as the table row. */}
                  <div className="d-flex align-items-center" style={{ gap: 8, flexShrink: 0, marginTop: 2 }}>
                    {faculty && canEditThread && this.state.editingKey !== `m${m.id}` ? (
                      <i
                        className="fal fa-pen"
                        title="Edit message"
                        onClick={() => this.startEditMessage(m)}
                        style={{ cursor: "pointer", color: "#5b7c54", fontSize: 12 }}
                      />
                    ) : null}
                    {faculty && canDeleteThread && this.state.deleteMsgId !== m.id ? (
                      <i
                        className="fal fa-trash-alt"
                        title="Delete message"
                        onClick={() => this.askDeleteMessage(m)}
                        style={{ cursor: "pointer", color: "#ef4444", fontSize: 12 }}
                      />
                    ) : null}
                  </div>
                </div>
                {this.state.deleteMsgId === m.id ? (
                  <div>
                    <div style={{ fontSize: 12, color: "#0f172a", marginBottom: 6 }}>
                      Delete this message?
                    </div>
                    <div className="d-flex" style={{ gap: 6, justifyContent: "flex-end" }}>
                      <button
                        type="button"
                        onClick={this.cancelDeleteMessage}
                        style={{ border: "1px solid #cbd5e1", background: "#fff", color: "#475569", borderRadius: 6, fontSize: 11, fontWeight: 600, padding: "3px 10px", cursor: "pointer" }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => this.confirmDeleteMessage(m)}
                        style={{ border: "none", background: "#ef4444", color: "#fff", borderRadius: 6, fontSize: 11, fontWeight: 600, padding: "3px 12px", cursor: "pointer" }}
                      >
                        <i className="fal fa-trash-alt mr-1" />Delete
                      </button>
                    </div>
                  </div>
                ) : this.state.editingKey === `m${m.id}` ? (
                  <>
                    <textarea
                      value={this.state.editText}
                      onChange={(e) => this.setState({ editText: e.target.value })}
                      rows={3}
                      style={{ width: "100%", fontSize: 13, border: "1px solid #cbd5e1", borderRadius: 6, padding: "4px 6px" }}
                    />
                    {m.image_path && isImageSrc(m.image_path) ? (
                      <img src={m.image_path} alt="attachment" style={bubbleImg} />
                    ) : null}
                    {this.renderEditControls(() => this.saveEditMessage(m))}
                  </>
                ) : (
                  <>
                    {m.answer ? (
                      <div style={{ fontSize: 13, color: "#0f172a" }}>{m.answer}</div>
                    ) : null}
                    {m.image_path && isImageSrc(m.image_path) ? (
                      <img src={m.image_path} alt="attachment" style={bubbleImg} />
                    ) : null}
                    {/* Student's feedback on this answer: liked + star rating. */}
                    {m.is_liked === "1" || (m.rating && Number(m.rating) > 0) ? (
                      <div
                        className="d-flex align-items-center"
                        style={{ gap: 10, marginTop: 6, fontSize: 11, color: "#475569" }}
                        title="Student feedback"
                      >
                        {m.is_liked === "1" ? (
                          <span style={{ color: "#16a34a", fontWeight: 700 }}>
                            <i className="fal fa-thumbs-up mr-1" />Liked
                          </span>
                        ) : null}
                        {m.rating && Number(m.rating) > 0 ? (
                          <span style={{ color: "#f59e0b", fontWeight: 700 }}>
                            {"★".repeat(Math.min(5, Number(m.rating)))}
                            <span style={{ color: "#94a3b8", fontWeight: 400 }}>
                              {" "}
                              {Number(m.rating)}/5
                            </span>
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                    <div style={metaLine(faculty ? "#5b7c54" : "#94a3b8")}>
                      {formatDateTime(m.answered_time)}
                      {faculty ? (
                        <i
                          className={`${seenByStudent ? "fas fa-check-double" : "fal fa-check"}`}
                          title={seenByStudent ? "Seen by student" : "Sent"}
                          style={{
                            marginLeft: 5,
                            fontSize: seenByStudent ? 14 : 11,
                            fontWeight: 900,
                            color: seenByStudent ? "#53bdeb" : "#8fae86",
                          }}
                        />
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}

        {modalConvLoading ? (
          <div className="text-center text-muted" style={{ fontSize: 12, padding: "6px 0" }}>
            <i className="fal fa-spinner fa-spin mr-1"></i>Loading conversation…
          </div>
        ) : modalConversation.length === 0 ? (
          <div className="text-center text-muted" style={{ fontSize: 12, padding: "2px 0 8px" }}>
            {canEditThread ? "No replies yet — send the first answer below." : "No replies yet."}
          </div>
        ) : null}
      </div>

      {/* Composer — WhatsApp-style message bar, pinned at the bottom (never scrolls).
          Dropped entirely for a role without is_edit: they read the thread, but the
          reply box, attach button and status dropdown all write, so none are shown. */}
      {canEditThread ? (
      <div style={{ background: "#f0f2f5", borderTop: "1px solid #e2e8f0", padding: "8px 10px", flexShrink: 0 }}>
        {/* attached-image preview chip */}
        {answerImage ? (
          <div style={{ marginBottom: 8, display: "inline-block", position: "relative" }}>
            <img
              src={answerImage}
              alt="answer preview"
              style={{ maxHeight: 80, borderRadius: 8, border: "1px solid #e2e8f0", display: "block" }}
            />
            <button
              type="button"
              onClick={() => this.setState({ answerImage: "" })}
              title="Remove image"
              style={{
                position: "absolute", top: -8, right: -8, width: 20, height: 20, borderRadius: "50%",
                border: "none", background: "#ef4444", color: "#fff", cursor: "pointer", fontSize: 12, lineHeight: "20px", padding: 0,
              }}
            >
              ×
            </button>
          </div>
        ) : null}

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* message pill: attach + text */}
          <div
            style={{
              flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8,
              background: "#fff", border: "1px solid #e2e8f0", borderRadius: 24, padding: "4px 6px 4px 12px",
            }}
          >
            {/* attach image */}
            <label
              style={{ margin: 0, cursor: "pointer", color: "#54656f", fontSize: 18, lineHeight: 1, flexShrink: 0 }}
              title={answerImage ? "Image attached" : "Attach image"}
            >
              <i className={`fal fa-paperclip ${answerImage ? "text-success" : ""}`} />
              <input type="file" accept="image/*" onChange={this.handleAnswerImage} style={{ display: "none" }} />
            </label>

            {/* message text */}
            <input
              type="text"
              placeholder="Type a message"
              value={replyText}
              onChange={(e) => this.setState({ replyText: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSend && onSend) onSend();
              }}
              style={{ flex: 1, minWidth: 0, border: "none", outline: "none", fontSize: 14, padding: "8px 4px", background: "transparent" }}
            />
          </div>

          {/* status — beside the message bar (mandatory before sending) */}
          <Form.Control
            as="select"
            size="sm"
            value={this.state.replyStatus}
            onChange={(e) => this.setState({ replyStatus: e.target.value })}
            title={needStatus ? "Select a status (required)" : "Set status"}
            style={{
              width: 128, height: 38, flex: "0 0 auto", fontSize: 12, borderRadius: 20,
              border: needStatus ? "1.5px solid #ef4444" : undefined,
              boxShadow: needStatus ? "0 0 0 3px rgba(239,68,68,0.15)" : undefined,
            }}
          >
            <option value="">{needStatus ? "Status required…" : "Set status…"}</option>
            <option value={DOUBT_STATUS.PENDING}>Pending</option>
            <option value={DOUBT_STATUS.REVIEWING}>Reviewing</option>
            <option value={DOUBT_STATUS.SOLVED}>Completed</option>
            <option value={DOUBT_STATUS.IRRELEVANT}>Irrelevent</option>
          </Form.Control>

          {/* send */}
          <button
            type="button"
            onClick={() => canSend && onSend && onSend()}
            disabled={!canSend}
            title="Send"
            style={{
              border: "none", background: canSend ? "#25d366" : "#cbd5e1", color: "#fff",
              width: 38, height: 38, borderRadius: "50%", cursor: canSend ? "pointer" : "default",
              flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <i className="fal fa-paper-plane" />
          </button>
        </div>
      </div>
      ) : null}
      </div>
    );
  }

  // Answer modal — opened from the Dashboard table to solve a doubt.
  renderAnswerModal() {
    const d = this.state.doubts.find((x) => x.id === this.state.modalDoubtId);
    if (!d) return null;
    return (
      <Modal show onHide={this.closeAnswerModal} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>Answer Doubt #{d.id}</Modal.Title>
        </Modal.Header>
        {/* Flex column (no body scroll) — only the conversation scrolls internally
            and the composer stays pinned, so the modal never scrolls. The single
            10px body padding is the consistent inset (no extra inner padding). */}
        <Modal.Body style={{ padding: 10, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {/* Doubt header — identity. The doubt's title / text / image are shown as
              the first bubble of the conversation thread below. */}
          <div
            className="d-flex justify-content-between flex-wrap align-items-start"
            style={{ marginBottom: 8, flexShrink: 0 }}
          >
            <strong style={{ fontSize: 15, color: "#0f172a" }}>{d.title}</strong>
            <span style={S.meta}>
              {d.studentName} · {d.branch} · {d.subject}
            </span>
          </div>

          {/* Conversation fills the rest and scrolls internally (fill = true). */}
          <div style={{ flex: 1, minHeight: 360, display: "flex" }}>
            {this.renderConversationThread(d, () => this.submitAnswerModal(d.id), true)}
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  renderDashboard() {
    const visible = this.getVisible();
    const { activeTab, searchText, showSubmit, newDoubt, openDropdown, selectedStudentKey } = this.state;
    const { classOptions, sectionOptions } = this.getFilterOptions();

    // The table shows ONE row per student by default; clicking a student drills
    // into that student's individual doubts.
    const inStudentDetail = !!selectedStudentKey;
    const detailDoubts = inStudentDetail
      ? visible.filter((d) => this.studentKey(d) === selectedStudentKey)
      : [];
    const detailStudent = inStudentDetail ? detailDoubts[0] || null : null;
    const students = inStudentDetail ? [] : this.groupByStudent(visible);
    // Rows that actually get paginated/rendered depend on the mode.
    const rows = inStudentDetail ? detailDoubts : students;

    // Pagination — clamp the current page to the available range and slice the rows.
    const pageSize = this.state.pageSize || PAGE_SIZE;
    const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
    const page = Math.min(Math.max(1, this.state.page), totalPages);
    const startIdx = (page - 1) * pageSize;
    const pageItems = rows.slice(startIdx, startIdx + pageSize);

    return (
      <>
        {/* KPI cards */}
        <div style={S.kpiGrid}>
          {STAT_CARDS.map((c) => (
            <div key={c.key} style={S.kpiCard(c.color)}>
              <div style={S.kpiIcon(c.color)}>
                <i className={c.icon} />
              </div>
              <div>
                <div style={S.kpiLabel}>{c.label}</div>
                <div style={S.kpiValue}>{this.countByStatus(c.key)}</div>
                <div style={S.kpiSub}>{c.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Main panel */}
        <div style={S.panel}>
          {/* Header: Filters / Refresh / records */}
          <div style={S.panelHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={S.hIcon}>
                <i className="fal fa-sliders-h" />
              </div>
              <span style={{ fontWeight: 700, fontSize: 13, color: "#1f2937" }}>Filters</span>
              {this.anyFilterActive() ? (
                <button
                  onClick={this.resetFilters}
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                    background: "none",
                    border: "1px solid #e5e7eb",
                    borderRadius: 6,
                    padding: "3px 10px",
                    cursor: "pointer",
                  }}
                >
                  Reset filters
                </button>
              ) : null}
            </div>
            <span style={S.recordBadge}>
              {inStudentDetail
                ? `${detailDoubts.length} ${detailDoubts.length === 1 ? "doubt" : "doubts"}`
                : `${students.length} ${students.length === 1 ? "student" : "students"}`}
            </span>
          </div>

          {/* Simulated student submission */}
          {showSubmit ? (
            <div style={{ padding: "14px 20px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9" }}>
              <Row>
                <Col sm={4} className="mb-2">
                  <Form.Control
                    size="sm"
                    placeholder="Student name"
                    value={newDoubt.studentName}
                    onChange={(e) =>
                      this.setState({ newDoubt: { ...newDoubt, studentName: e.target.value } })
                    }
                  />
                </Col>
                <Col sm={2} className="mb-2">
                  <Form.Control
                    size="sm"
                    placeholder="Mobile"
                    value={newDoubt.mobile}
                    onChange={(e) =>
                      this.setState({ newDoubt: { ...newDoubt, mobile: e.target.value } })
                    }
                  />
                </Col>
                <Col sm={2} className="mb-2">
                  <Form.Control
                    as="select"
                    size="sm"
                    value={newDoubt.branch}
                    onChange={(e) =>
                      this.setState({ newDoubt: { ...newDoubt, branch: e.target.value } })
                    }
                  >
                    <option value="">Branch</option>
                    {this.state.allBranches.map((b) => (
                      <option key={b.id} value={b.branch_name}>{b.branch_name}</option>
                    ))}
                  </Form.Control>
                </Col>
                <Col sm={2} className="mb-2">
                  <Form.Control
                    as="select"
                    size="sm"
                    value={newDoubt.subject}
                    onChange={(e) =>
                      this.setState({
                        newDoubt: {
                          ...newDoubt,
                          subject: e.target.value,
                          // Auto-fill the routed faculty for the new subject (override-able).
                          faculty: this.facultyForSubject(e.target.value),
                        },
                      })
                    }
                  >
                    <option value="">Subject</option>
                    {this.state.masterSubjects.map((s) => (
                      <option key={s.id} value={s.subject}>{s.subject}</option>
                    ))}
                  </Form.Control>
                </Col>
                <Col sm={2} className="mb-2">
                  {/* Faculty narrowed to the chosen subject (education_mngt subject_ids). */}
                  <Form.Control
                    as="select"
                    size="sm"
                    value={newDoubt.faculty}
                    disabled={!newDoubt.subject}
                    onChange={(e) =>
                      this.setState({ newDoubt: { ...newDoubt, faculty: e.target.value } })
                    }
                  >
                    <option value="">{newDoubt.subject ? "Faculty" : "Select subject first"}</option>
                    {this.facultyListForSubject(newDoubt.subject).map((f) => (
                      <option key={f.id} value={f.name}>{f.name}</option>
                    ))}
                  </Form.Control>
                </Col>
                <Col sm={2} className="mb-2">
                  {/* Chapter is optional and independent of the chosen subject. */}
                  <Form.Control
                    as="select"
                    size="sm"
                    value={newDoubt.chapter}
                    onChange={(e) =>
                      this.setState({ newDoubt: { ...newDoubt, chapter: e.target.value } })
                    }
                  >
                    <option value="">Chapter (optional)</option>
                    {this.state.masterChapters.map((c) => (
                      <option key={c.id} value={c.chapter}>{c.chapter}</option>
                    ))}
                  </Form.Control>
                </Col>
                <Col sm={8} className="mb-2">
                  <Form.Control
                    size="sm"
                    placeholder="Type the doubt..."
                    value={newDoubt.title}
                    onChange={(e) =>
                      this.setState({ newDoubt: { ...newDoubt, title: e.target.value } })
                    }
                  />
                </Col>
                <Col sm={4} className="mb-2">
                  <label style={{ fontSize: 11, color: "#475569", display: "block", marginBottom: 2 }}>
                    {newDoubt.image ? "Image attached ✓" : "Attach image (optional)"}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={this.handleNewImage}
                    style={{ fontSize: 12 }}
                  />
                </Col>
              </Row>
              {newDoubt.image ? (
                <img
                  src={newDoubt.image}
                  alt="attachment preview"
                  style={{
                    maxHeight: 90,
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                    marginBottom: 10,
                    display: "block",
                  }}
                />
              ) : null}
              <Button size="sm" variant="primary" onClick={this.submitNewDoubt}>
                Submit doubt
              </Button>
            </div>
          ) : null}

          {/* Filter dropdowns (Certificate-dashboard style, real backend data) */}
          <div style={S.filterBar} ref={this.filterRef}>
            <div style={S.filterGrid}>
              <MultiSelect
                label="Class"
                icon="fal fa-graduation-cap"
                options={classOptions}
                selected={this.state.fClass}
                onChange={this.onClassChange}
                onToggleOpen={() => this.setState({ openDropdown: openDropdown === "class" ? null : "class" })}
                isOpen={openDropdown === "class"}
              />
              <MultiSelect
                label="Section"
                icon="fal fa-layer-group"
                options={sectionOptions}
                selected={this.state.fSection}
                onChange={(val) => this.setState({ fSection: val })}
                onToggleOpen={() => this.setState({ openDropdown: openDropdown === "section" ? null : "section" })}
                isOpen={openDropdown === "section"}
              />
              {/* Date range filter — same picker as the Expense dashboard. */}
              <div>
                <div style={S.fLabel}>
                  <i className="fal fa-calendar-alt" style={{ marginRight: 6 }} />Date Range
                </div>
                {/* Make the picker's fixed-width trigger fill its filter column. */}
                <style>{`
                  .doubt-daterange,
                  .doubt-daterange .customDaterange,
                  .doubt-daterange #DateTimeRangeContainerNoMobileMode { width: 100% !important; max-width: 100% !important; }
                  .doubt-daterange .customDaterange input,
                  .doubt-daterange #DateTimeRangeContainerNoMobileMode input { width: 100% !important; }
                `}</style>
                <div className="doubt-daterange">
                  <DateBlock
                    key={`${this.state.fDateFrom}|${this.state.fDateTo}`}
                    applyCallback={this.handleDateBlockCallback}
                    initialStart={this.state.fDateFrom || undefined}
                    initialEnd={this.state.fDateTo || undefined}
                    leftMode
                  />
                </div>
              </div>
              {(this.state.fDateFrom || this.state.fDateTo) ? (
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    type="button"
                    onClick={() => this.setState({ fDateFrom: "", fDateTo: "", page: 1 })}
                    style={{
                      height: 38, padding: "0 14px", border: "1.5px solid #d1d5db",
                      borderRadius: 8, background: "#fff", color: "#4b5563", fontSize: 13,
                      fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
                    }}
                  >
                    <i className="fal fa-times" />Clear dates
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          {/* Search + status pills */}
          <div style={S.toolRow}>
            <div style={{ position: "relative" }}>
              <i
                className="fal fa-search"
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#9ca3af",
                  fontSize: 13,
                  pointerEvents: "none",
                }}
              />
              <input
                type="text"
                placeholder="Search student, doubt…"
                value={searchText}
                onChange={(e) => this.setState({ searchText: e.target.value, page: 1 })}
                style={S.search}
              />
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {STATUS_PILLS.map((key) => {
                const active = activeTab === key;
                const color = key === "All" ? PURPLE : STATUS_COLOR[key];
                return (
                  <button
                    key={key}
                    style={S.pill(active, color)}
                    onClick={() => this.setState({ activeTab: key, expandedId: null, page: 1 })}
                  >
                    {key}
                    <span style={S.pillCount(active, color)}>{this.countByStatus(key)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Doubt table — one row per student; click a student to drill in. */}
          <div style={{ padding: "4px 12px 12px" }}>
            {inStudentDetail ? (
              <div className="d-flex align-items-center" style={{ gap: 12, padding: "6px 4px 12px" }}>
                <button
                  type="button"
                  onClick={() => this.setState({ selectedStudentKey: null, page: 1 })}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "1.5px solid #d1d5db", background: "#fff", color: "#4b5563", borderRadius: 8, padding: "6px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                >
                  <i className="fal fa-arrow-left" />Back to students
                </button>
                <div>
                  <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 15 }}>
                    <i className="fal fa-user-graduate mr-2" style={{ color: PURPLE }} />
                    {detailStudent ? detailStudent.studentName : "Student"}
                  </div>
                  <div style={S.meta}>
                    {detailDoubts.length} {detailDoubts.length === 1 ? "doubt" : "doubts"}
                    {detailStudent && detailStudent.class ? ` · ${detailStudent.class}` : ""}
                    {detailStudent && detailStudent.mobile ? ` · ${detailStudent.mobile}` : ""}
                  </div>
                </div>
              </div>
            ) : null}
            {rows.length === 0 ? (
              <div className="text-center text-muted py-5">
                <i className="fal fa-inbox fa-2x mb-2 d-block"></i>
                No doubts match the current filters.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      {inStudentDetail ? (
                        <>
                          <th style={S.th}>Student</th>
                          <th style={S.th}>Class</th>
                          <th style={S.th}>Subject</th>
                          <th style={S.th}>Chapter</th>
                          <th style={S.th}>Doubt</th>
                          <th style={S.th}>Faculty Name</th>
                          <th style={S.th}>Status</th>
                          <th style={S.th}>Submitted</th>
                        </>
                      ) : (
                        <>
                          <th style={S.th}>Student</th>
                          <th style={S.th}>Adm No</th>
                          <th style={S.th}>Class</th>
                          <th style={S.th}>Section</th>
                          <th style={{ ...S.th, textAlign: "center" }}>Doubts</th>
                          <th style={{ ...S.th, textAlign: "center" }}>Unsolved</th>
                          <th style={S.th}>Latest</th>
                        </>
                      )}
                      <th
                        style={{
                          ...S.th,
                          textAlign: "right",
                          position: "sticky",
                          right: 0,
                          background: "#fff",
                          zIndex: 3,
                          boxShadow: "-4px 0 6px -4px rgba(0,0,0,0.12)",
                        }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {!inStudentDetail
                      ? pageItems.map((s) => (
                          <tr
                            key={s.key}
                            style={{ cursor: "pointer" }}
                            onClick={() => this.openStudentDoubts(s.key)}
                            title="View this student's doubts"
                          >
                            <td style={{ ...S.td, whiteSpace: "nowrap" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <div style={{ ...S.avatar, width: 34, height: 34, fontSize: 12, background: "#e0e7ff", color: "#4338ca", flexShrink: 0 }}>
                                  {initials(s.name)}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontWeight: 600, color: "#0f172a" }}>{s.name}</div>
                                </div>
                              </div>
                            </td>
                            <td style={S.td}>
                              {s.admissionNo ? (
                                <span style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{s.admissionNo}</span>
                              ) : (
                                <span style={{ ...S.meta, color: "#94a3b8" }}>—</span>
                              )}
                            </td>
                            <td style={S.td}>
                              <span style={{ fontSize: 12, color: "#475569" }}>
                                {s.class || "—"}
                              </span>
                            </td>
                            <td style={S.td}>
                              <span style={{ fontSize: 12, color: "#475569" }}>
                                {s.section || "—"}
                              </span>
                            </td>
                            <td style={{ ...S.td, textAlign: "center" }}>
                              <span style={{ display: "inline-block", minWidth: 26, background: `${PURPLE}14`, color: PURPLE, borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>
                                {s.total}
                              </span>
                            </td>
                            <td style={{ ...S.td, textAlign: "center" }}>
                              <span style={{ fontWeight: 700, color: s.notSolved ? STATUS_COLOR[DOUBT_STATUS.PENDING] : "#cbd5e1" }}>
                                {s.notSolved}
                              </span>
                            </td>
                            <td style={S.td}>
                              {/* Full date + time, with the relative time below. */}
                              <div style={{ fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>
                                {formatDateTime(s.latestAt) || "—"}
                              </div>
                              {s.latestAt ? (
                                <div style={{ ...S.meta, marginTop: 2 }}>{timeAgo(s.latestAt)}</div>
                              ) : null}
                            </td>
                            <td
                              style={{
                                ...S.td, textAlign: "right", whiteSpace: "nowrap",
                                position: "sticky", right: 0, background: "#fff", zIndex: 1,
                                boxShadow: "-4px 0 6px -4px rgba(0,0,0,0.12)",
                              }}
                            >
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); this.openStudentDoubts(s.key); }}
                                style={S.actionBtn(PURPLE)}
                              >
                                View doubts<i className="fal fa-chevron-right ml-1" />
                              </button>
                            </td>
                          </tr>
                        ))
                      : pageItems.map((d) => (
                      <tr key={d.id}>
                        <td style={{ ...S.td, whiteSpace: "nowrap" }}>
                          <span style={{ fontWeight: 600, color: "#0f172a" }}>
                            {d.studentName}
                          </span>
                        </td>
                        <td style={S.td}>
                          <span style={{ fontSize: 12, color: "#475569" }}>{d.class || "—"}</span>
                        </td>
                        <td style={S.td}>
                          {/* Highlighted subject tag */}
                          {d.subject ? (
                            <span
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 5,
                                background: `${PURPLE}14`, color: PURPLE, borderRadius: 6,
                                padding: "2px 9px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
                              }}
                            >
                              <i className="fal fa-book" />
                              {d.subject}
                            </span>
                          ) : (
                            <span style={{ ...S.meta, color: "#94a3b8" }}>—</span>
                          )}
                        </td>
                        <td style={S.td}>
                          <span style={{ fontSize: 12, color: "#475569" }}>{d.chapter || "—"}</span>
                        </td>
                        <td style={S.td}>
                          {/* Short title (e.g. "title…"); hover shows the full text.
                              The badge alerts the lecturer when the student is waiting:
                              red = new/unanswered, orange = student re-asked. */}
                          <div className="d-flex align-items-center" style={{ gap: 6 }}>
                            <span
                              title={d.title || ""}
                              style={{
                                color: "#0f172a", cursor: "default",
                                display: "inline-block", maxWidth: 200,
                                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                verticalAlign: "middle",
                              }}
                            >
                              {d.title || "—"}
                            </span>
                            {d.awaitingReply && d.answer ? (
                              <span
                                style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  background: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa",
                                  borderRadius: 10, padding: "1px 8px", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap",
                                }}
                                title="The student asked again in this conversation — please reply"
                              >
                                <i className="fal fa-reply" />
                                Re-asked{d.pendingCount > 1 ? ` (${d.pendingCount})` : ""}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td style={S.td}>
                          {(() => {
                            // Reply Faculty: the FACULTY who answered this doubt —
                            // never the student. Source it from the claim (assigned_
                            // faculty_name is always a real employee), falling back to
                            // the answer's author only when the doubt is claimed. Blank
                            // until a faculty actually replies.
                            const replier =
                              d.assignedFacultyId && d.assignedFaculty && d.assignedFaculty !== "Unassigned"
                                ? d.assignedFaculty
                                : "";
                            return replier ? (
                              <span
                                style={{
                                  display: "inline-flex", alignItems: "center", gap: 6,
                                  background: "#dcfce7", color: "#15803d", borderRadius: 20,
                                  padding: "3px 10px", fontSize: 12, fontWeight: 600,
                                }}
                              >
                                <i className="fal fa-user-check" style={{ flexShrink: 0 }} />
                                {replier}
                              </span>
                            ) : (
                              <span style={{ ...S.meta, color: "#94a3b8" }}>—</span>
                            );
                          })()}
                        </td>
                        <td style={S.td}>
                          <span style={S.badge(this.effectiveStatus(d))}>
                            <span style={S.dot}></span>
                            {this.effectiveStatus(d)}
                          </span>
                        </td>
                        <td style={S.td}>
                          <span style={S.meta}>{timeAgo(d.createdAt)}</span>
                        </td>
                        <td
                          style={{
                            ...S.td,
                            textAlign: "right",
                            whiteSpace: "nowrap",
                            position: "sticky",
                            right: 0,
                            background: "#fff",
                            zIndex: 1,
                            boxShadow: "-4px 0 6px -4px rgba(0,0,0,0.12)",
                          }}
                        >
                          {/* Actions follow the role's grants on this module: Edit
                              needs is_edit, Delete needs is_delete. A role with
                              neither still has is_view (it's how they got here), so
                              they keep a read-only View into the conversation rather
                              than an empty Actions cell. */}
                          <div style={{ display: "inline-flex", gap: 6 }}>
                            <button
                              type="button"
                              title={d.answer ? "Edit answer" : "Answer doubt"}
                              onClick={() => this.openAnswerModal(d)}
                              style={S.actionBtn(PURPLE)}
                            >
                              <i className="fal fa-pen" />Edit
                            </button>
                            {this.canEditDoubts() ? (
                              <button
                                type="button"
                                title={d.answer ? "Edit answer" : "Answer doubt"}
                                onClick={() => this.openAnswerModal(d)}
                                style={S.actionBtn(PURPLE)}
                              >
                                <i className="fal fa-pen" />Edit
                              </button>
                            ) : (
                              <button
                                type="button"
                                title="View doubt"
                                onClick={() => this.openAnswerModal(d)}
                                style={S.actionBtn("#64748b")}
                              >
                                <i className="fal fa-eye" />View
                              </button>
                            )}
                            {this.canDeleteDoubts() ? (
                              <button
                                type="button"
                                title="Delete"
                                onClick={() => this.deleteDoubt(d.id)}
                                style={S.actionBtn("#ef4444")}
                              >
                                <i className="fal fa-trash-alt" />Delete
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {rows.length > 0 ? (
              <div
                className="d-flex align-items-center justify-content-between flex-wrap"
                style={{ marginTop: 12, gap: 10 }}
              >
                <div className="d-flex align-items-center" style={{ gap: 10, flexWrap: "wrap" }}>
                  <span style={S.meta}>
                    Showing {startIdx + 1}–{Math.min(startIdx + pageSize, rows.length)} of{" "}
                    {rows.length} {inStudentDetail ? "doubts" : "students"}
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={S.meta}>Rows per page</span>
                    <Form.Control
                      as="select"
                      size="sm"
                      value={pageSize}
                      onChange={(e) => this.setState({ pageSize: Number(e.target.value), page: 1 })}
                      style={{ width: 72, height: 30, fontSize: 12, borderRadius: 8, padding: "0 6px" }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </Form.Control>
                  </span>
                </div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <button
                    type="button"
                    onClick={() => this.setState({ page: page - 1 })}
                    disabled={page <= 1}
                    style={S.pageBtn(false, page <= 1)}
                    title="Previous"
                  >
                    <i className="fal fa-chevron-left" />
                  </button>
                  {this.pageNumbers(page, totalPages).map((p, i) =>
                    p === "…" ? (
                      <span key={`e${i}`} style={{ padding: "0 6px", color: "#94a3b8", fontSize: 12 }}>
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        type="button"
                        onClick={() => this.setState({ page: p })}
                        style={S.pageBtn(p === page, false)}
                      >
                        {p}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    onClick={() => this.setState({ page: page + 1 })}
                    disabled={page >= totalPages}
                    style={S.pageBtn(false, page >= totalPages)}
                    title="Next"
                  >
                    <i className="fal fa-chevron-right" />
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </>
    );
  }

  // Build a compact page-number list with ellipses (e.g. 1 … 4 5 6 … 12).
  pageNumbers(page, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const out = [1];
    const from = Math.max(2, page - 1);
    const to = Math.min(total - 1, page + 1);
    if (from > 2) out.push("…");
    for (let p = from; p <= to; p++) out.push(p);
    if (to < total - 1) out.push("…");
    out.push(total);
    return out;
  }



  // Assignments tab — a doubts inbox: the full doubt list on the left, the selected
  // doubt's conversation + reassign on the right. If a student was opened from the
  // dashboard's "View doubts" it's scoped to that student; otherwise it shows all
  // doubts. (The old per-faculty workload table has been removed.)
  renderAssignments() {
    const studentKey = this.state.workloadStudentKey;
    const all = studentKey
      ? this.state.doubts.filter((d) => this.studentKey(d) === studentKey)
      : this.state.doubts;
    // The currently open doubt (right pane) — in "All Doubts" mode the header shows
    // the selected doubt's student; before any selection it just says "All Doubts".
    const activeDoubt = this.state.doubts.find((d) => d.id === this.state.workloadDoubtId) || null;
    const name = studentKey
      ? all[0] ? all[0].studentName : "Student"
      : activeDoubt ? activeDoubt.studentName : "All Doubts";
    const showStudent = !!(studentKey || activeDoubt);
    const sq = (this.state.studentDoubtSearch || "").trim().toLowerCase();
    // Search-filtered list, then the status filter buttons (All / Unsolved / Solved).
    const searched = sq
      ? all.filter((d) =>
          `${d.title || ""} ${d.description || ""} ${d.subject || ""} ${d.studentName || ""}`
            .toLowerCase()
            .includes(sq)
        )
      : all;
    const solvedCount = searched.filter((d) => this.effectiveStatus(d) === DOUBT_STATUS.SOLVED).length;
    const statusFilters = [
      { key: "all", label: "All", count: searched.length, color: PURPLE },
      { key: "unsolved", label: "Unsolved", count: searched.length - solvedCount, color: STATUS_COLOR[DOUBT_STATUS.PENDING] },
      { key: "solved", label: "Solved", count: solvedCount, color: STATUS_COLOR[DOUBT_STATUS.SOLVED] },
    ];
    const assignFilter = this.state.assignFilter;
    let ordered = searched;
    if (assignFilter === "solved") ordered = searched.filter((d) => this.effectiveStatus(d) === DOUBT_STATUS.SOLVED);
    else if (assignFilter === "unsolved") ordered = searched.filter((d) => this.effectiveStatus(d) !== DOUBT_STATUS.SOLVED);
    return (
      <div style={S.panel}>
        <div style={S.panelHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {/* Search the doubts */}
            <div style={{ position: "relative" }}>
              <i
                className="fal fa-search"
                style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: 13, pointerEvents: "none" }}
              />
              <input
                type="text"
                placeholder="Search doubt…"
                value={this.state.studentDoubtSearch}
                onChange={(e) => this.setState({ studentDoubtSearch: e.target.value })}
                style={{ ...S.search, width: 240 }}
              />
            </div>
          </div>
          {/* Student icon + name — centered in the header */}
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, minWidth: 0 }}>
            <div style={S.hIcon}>
              <i className={`fal fa-${showStudent ? "user-graduate" : "comments"}`} />
            </div>
            <span style={{ fontWeight: 700, fontSize: 15, color: "#1f2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {name}
            </span>
          </div>
          {/* Status filter — All / Unsolved / Solved, each with a count. */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {statusFilters.map((b) => {
              const active = assignFilter === b.key;
              return (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => this.setState({ assignFilter: b.key })}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    border: `1.5px solid ${active ? b.color : "#e5e7eb"}`,
                    background: active ? b.color : "#fff",
                    color: active ? "#fff" : "#4b5563",
                    borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                  }}
                >
                  {b.label}
                  <span
                    style={{
                      background: active ? "rgba(255,255,255,0.25)" : `${b.color}22`,
                      color: active ? "#fff" : b.color,
                      borderRadius: 10, padding: "0 6px", fontSize: 11, fontWeight: 700, minWidth: 16, textAlign: "center",
                    }}
                  >
                    {b.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        {/* Fixed-height frame so the two panes fill it and scroll internally. */}
        <div style={{ height: "72vh", overflow: "hidden" }}>
          {this.renderWorkloadPanes()}
        </div>
      </div>
    );
  }

  // Faculty Report tab (MAIN-COLLEGE / management login only) — one row per
  // employee showing how many doubts they received (were assigned/claimed), how
  // many they solved, and how many are still pending. Counting is SINGLE-OWNER
  // (by assigned faculty) so every doubt is counted once — no double-counting
  // across co-teachers. Unclaimed doubts (the shared pool) are shown separately
  // as "Unassigned" so the numbers always reconcile with the total.
  renderFacultyReport() {
    const { doubts, masterFaculty } = this.state;
    const isSolved = (d) => this.effectiveStatus(d) === DOUBT_STATUS.SOLVED;
    const totalDoubts = doubts.length;
    const totalSolved = doubts.filter(isSolved).length;
    const totalPending = totalDoubts - totalSolved;
    const unassigned = doubts.filter((d) => !d.assignedFacultyId).length;

    // Status filter (buttons) — scopes the doubts the per-employee table counts.
    const reportFilter = this.state.reportFilter || "all";
    const scoped =
      reportFilter === "solved"
        ? doubts.filter(isSolved)
        : reportFilter === "pending"
        ? doubts.filter((d) => !isSolved(d))
        : doubts;
    const filterButtons = [
      { key: "all", label: "All", count: totalDoubts, color: PURPLE },
      { key: "solved", label: "Solved", count: totalSolved, color: STATUS_COLOR[DOUBT_STATUS.SOLVED] },
      { key: "pending", label: "Pending", count: totalPending, color: STATUS_COLOR[DOUBT_STATUS.PENDING] },
    ];

    const rows = masterFaculty
      .map((f) => {
        const got = scoped.filter((d) => String(d.assignedFacultyId) === String(f.id));
        const solved = got.filter(isSolved).length;
        return {
          id: f.id,
          name: f.name,
          subjects: (f.subjectNames && f.subjectNames.length ? f.subjectNames.join(", ") : "—"),
          got: got.length,
          solved,
          pending: got.length - solved,
          pct: got.length ? Math.round((solved / got.length) * 100) : 0,
        };
      })
      .filter((r) => r.got > 0)
      .sort((a, b) => b.got - a.got);

    // Pagination over the employee rows.
    const pageSize = 10;
    const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
    const page = Math.min(Math.max(1, this.state.reportPage || 1), totalPages);
    const startIdx = (page - 1) * pageSize;
    const pageRows = rows.slice(startIdx, startIdx + pageSize);

    const cards = [
      { label: "Total Doubts", value: totalDoubts, color: PURPLE, icon: "fal fa-comments", sub: "All doubts" },
      { label: "Solved", value: totalSolved, color: STATUS_COLOR[DOUBT_STATUS.SOLVED], icon: "fal fa-check-circle", sub: "Completed" },
      { label: "Pending", value: totalDoubts - totalSolved, color: STATUS_COLOR[DOUBT_STATUS.PENDING], icon: "fal fa-clock", sub: "Not yet solved" },
      { label: "Awaiting Faculty", value: unassigned, color: "#94a3b8", icon: "fal fa-inbox", sub: "No faculty has taken these yet" },
    ];

    return (
      <>
        {/* KPI summary */}
        <div style={S.kpiGrid}>
          {cards.map((c) => (
            <div key={c.label} style={S.kpiCard(c.color)}>
              <div style={S.kpiIcon(c.color)}>
                <i className={c.icon} />
              </div>
              <div>
                <div style={S.kpiLabel}>{c.label}</div>
                <div style={S.kpiValue}>{c.value}</div>
                <div style={S.kpiSub}>{c.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Per-employee table */}
        <div style={S.panel}>
          <div style={S.panelHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={S.hIcon}>
                <i className="fal fa-chalkboard-teacher" />
              </div>
              <span style={{ fontWeight: 700, fontSize: 13, color: "#1f2937" }}>
                Employee Doubt Report
              </span>
            </div>
            {/* Filter the doubts counted: All / Solved / Pending. */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              {filterButtons.map((b) => {
                const active = reportFilter === b.key;
                return (
                  <button
                    key={b.key}
                    type="button"
                    onClick={() => this.setState({ reportFilter: b.key, reportPage: 1 })}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 5,
                      border: `1.5px solid ${active ? b.color : "#e5e7eb"}`,
                      background: active ? b.color : "#fff",
                      color: active ? "#fff" : "#4b5563",
                      borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                    }}
                  >
                    {b.label}
                    <span
                      style={{
                        background: active ? "rgba(255,255,255,0.25)" : `${b.color}22`,
                        color: active ? "#fff" : b.color,
                        borderRadius: 10, padding: "0 6px", fontSize: 11, fontWeight: 700, minWidth: 16, textAlign: "center",
                      }}
                    >
                      {b.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {rows.length === 0 ? (
            <div style={{ ...S.meta, padding: "18px 20px" }}>
              No doubts have been assigned to any employee yet.
            </div>
          ) : (
            <div style={{ padding: "4px 12px 12px", overflowX: "auto" }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Employee</th>
                    <th style={S.th}>Subjects</th>
                    <th style={{ ...S.th, textAlign: "center" }}>Received</th>
                    <th style={{ ...S.th, textAlign: "center" }}>Solved</th>
                    <th style={{ ...S.th, textAlign: "center" }}>Pending</th>
                    <th style={S.th}>Solved %</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r) => (
                    <tr key={r.id}>
                      <td style={S.td}>
                        <div className="d-flex align-items-center" style={{ gap: 10 }}>
                          <div style={{ ...S.avatar, width: 32, height: 32, fontSize: 12, background: "#e0e7ff", color: "#4338ca", flexShrink: 0 }}>
                            {initials(r.name)}
                          </div>
                          <span style={{ fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap" }}>{r.name}</span>
                        </div>
                      </td>
                      <td style={S.td}>
                        <span style={{ fontSize: 12, color: "#64748b" }}>{r.subjects}</span>
                      </td>
                      <td style={{ ...S.td, textAlign: "center", fontWeight: 800, color: "#0f172a" }}>{r.got}</td>
                      <td style={{ ...S.td, textAlign: "center" }}>
                        <span style={{ fontWeight: 700, color: r.solved ? STATUS_COLOR[DOUBT_STATUS.SOLVED] : "#cbd5e1" }}>{r.solved}</span>
                      </td>
                      <td style={{ ...S.td, textAlign: "center" }}>
                        <span style={{ fontWeight: 700, color: r.pending ? STATUS_COLOR[DOUBT_STATUS.PENDING] : "#cbd5e1" }}>{r.pending}</span>
                      </td>
                      <td style={{ ...S.td, minWidth: 160 }}>
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                          <div style={{ flex: 1, height: 7, borderRadius: 6, background: "#f1f5f9", overflow: "hidden", minWidth: 70 }}>
                            <div style={{ width: `${r.pct}%`, height: "100%", background: STATUS_COLOR[DOUBT_STATUS.SOLVED] }} />
                          </div>
                          <span style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap" }}>{r.pct}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {rows.length > pageSize ? (
                <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ marginTop: 12, gap: 10, padding: "0 4px" }}>
                  <span style={S.meta}>
                    Showing {startIdx + 1}–{Math.min(startIdx + pageSize, rows.length)} of {rows.length} employees
                  </span>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <button
                      type="button"
                      onClick={() => this.setState({ reportPage: Math.max(1, page - 1) })}
                      disabled={page <= 1}
                      style={S.pageBtn(false, page <= 1)}
                    >
                      <i className="fal fa-chevron-left" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => this.setState({ reportPage: p })}
                        style={S.pageBtn(p === page, false)}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => this.setState({ reportPage: Math.min(totalPages, page + 1) })}
                      disabled={page >= totalPages}
                      style={S.pageBtn(false, page >= totalPages)}
                    >
                      <i className="fal fa-chevron-right" />
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </>
    );
  }

  // Notifications tab — activity feed derived from the doubts (Story 6).
  renderNotifications() {
    const events = [];
    this.state.doubts.forEach((d) => {
      events.push({
        id: `new-${d.id}`,
        icon: "fal fa-comment-alt-plus",
        color: STATUS_COLOR[DOUBT_STATUS.PENDING],
        title: `New doubt from ${d.studentName}`,
        text: d.title,
        sub: `${d.branch} · ${d.subject}`,
        time: d.createdAt,
      });
      if (d.answer) {
        events.push({
          id: `ans-${d.id}`,
          icon: "fal fa-check-circle",
          color: STATUS_COLOR[DOUBT_STATUS.SOLVED],
          title: `Answered by ${d.answer.answeredBy}`,
          text: d.title,
          sub: `for ${d.studentName}`,
          time: d.answer.answeredAt,
        });
      }
    });
    events.sort((a, b) => new Date(b.time) - new Date(a.time));

    return (
      <div style={S.panel}>
        <div style={S.panelHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={S.hIcon}>
              <i className="fal fa-bell" />
            </div>
            <span style={{ fontWeight: 700, fontSize: 13, color: "#1f2937" }}>
              Notification History
            </span>
          </div>
          <span style={S.recordBadge}>
            {events.length} {events.length === 1 ? "event" : "events"}
          </span>
        </div>
        <div style={{ padding: 16 }}>
          {events.length === 0 ? (
            <div className="text-center text-muted py-5">
              <i className="fal fa-bell-slash fa-2x mb-2 d-block"></i>
              No notifications yet.
            </div>
          ) : (
            events.map((ev) => (
              <div
                key={ev.id}
                className="d-flex align-items-start"
                style={{
                  padding: "12px 8px",
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: `${ev.color}18`,
                    color: ev.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    fontSize: 15,
                  }}
                >
                  <i className={ev.icon} />
                </div>
                <div className="ml-2 flex-grow-1">
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
                    {ev.title}
                  </div>
                  <div style={{ fontSize: 13, color: "#334155" }}>{ev.text}</div>
                  <div style={S.meta}>{ev.sub}</div>
                </div>
                <div style={{ ...S.meta, whiteSpace: "nowrap" }}>{timeAgo(ev.time)}</div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  render() {
    const { activeView } = this.state;
    return (
      <div style={S.page}>
        <div style={S.shell}>
          {/* Top module tabs */}
          <div style={S.tabStrip}>
            <ul style={S.navList}>
              {/* The Faculty Report tab is management-only (main-college login). */}
              {(this.isMainCollege()
                ? [...VIEW_TABS, { key: "report", label: "Faculty Report", icon: "fal fa-chart-bar" }]
                : VIEW_TABS
              ).map((t) => {
                const active = activeView === t.key;
                // Status workspace tabs show a live count badge.
                const STATUS_FOR_TAB = {
                  pending: DOUBT_STATUS.PENDING,
                  review: DOUBT_STATUS.REVIEWING,
                  solved: DOUBT_STATUS.SOLVED,
                };
                const st = STATUS_FOR_TAB[t.key];
                const count = st ? this.countByStatus(st) : null;
                const color = st ? STATUS_COLOR[st] : PURPLE;
                return (
                  <li key={t.key}>
                    <a
                      href="#"
                      style={S.navTab(active)}
                      onClick={(e) => {
                        e.preventDefault();
                        if (t.key === activeView) {
                          // Re-clicking the Assignment tab exits the inline student
                          // doubts view back to the Faculty Workload table (there is
                          // no Back button).
                          if (t.key === "assignments" && this.state.workloadStudentKey) {
                            this.setState({
                              workloadStudentKey: null,
                              studentDoubtSearch: "",
                              workloadDoubtId: null,
                              modalConversation: [],
                              convDoubtId: null,
                            });
                          }
                          return;
                        }
                        // Switch tab and close any open dropdown / modal. Returning
                        // to the Dashboard refreshes its filter dropdowns back to
                        // the default (all-selected) state.
                        this.setState({
                          activeView: t.key,
                          openDropdown: null,
                          // Close any open modal / inline student view across tabs.
                          workloadFaculty: null,
                          workloadStudentKey: null,
                          studentDoubtSearch: "",
                          workloadDoubtId: null,
                          modalDoubtId: null,
                          modalConversation: [],
                          convDoubtId: null,
                          editingKey: null,
                          editText: "",
                          editTitle: "",
                        });
                        if (t.key === "dashboard") this.resetFilters();
                      }}
                    >
                      <i className={t.icon} />
                      {t.label}
                      {count != null ? (
                        <span style={S.pillCount(active, color)}>{count}</span>
                      ) : null}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Tab content */}
          <div style={S.body}>
            {activeView === "assignments"
              ? this.renderAssignments()
              : activeView === "report" && this.isMainCollege()
              ? this.renderFacultyReport()
              : this.renderDashboard()}
          </div>
        </div>

        {this.renderAnswerModal()}
        {this.renderWorkloadModal()}
        {this.renderDeleteConfirm()}
      </div>
    );
  }
}

export default withApollo(StudentDoubtsComponent);
